/**
 * Loumars CMS - WebMCP (Web Model Context Protocol) Imperative Tools Runtime
 * Enables autonomous browsing agents to inspect metadata, query theme state,
 * discover rendered widgets, and interact with site services.
 */

import {
  getThemeTokens as getCoreThemeTokens,
  validateThemeContrast,
  generateTailwindThemeCss,
  type ThemeSlug,
} from '@loumars/client-core';
import {
  CANONICAL_SUITES,
  CANONICAL_AMENITIES,
  calculateSuiteQuote,
  type LuxuryCurrencyCode,
  calculateCargoEstimate,
} from './custom-elements';
import {
  calculateEmissionsEstimate,
  type EmissionsCalculationParams,
  type EmissionsCalculationResult,
} from './emissions-engine';
import {
  queryTradeCorridors,
  TRADE_CORRIDOR_REGISTRY,
  type TradeCorridorInfo,
  type TradeCorridorsQueryParams,
  type TradeCorridorsResult,
} from './trade-corridors';
import {
  queryFleet,
  FLEET_REGISTRY,
  type FleetItemInfo,
  type FleetQueryParams,
  type FleetResult,
} from './fleet-engine';
import {
  queryHubs,
  GLOBAL_HUBS_REGISTRY,
  type HubInfo,
  type HubsQueryParams,
  type HubsResult,
} from './hubs-engine';
import {
  queryColdChain,
  COLD_CHAIN_REGISTRY,
  type ColdChainZoneInfo,
  type ColdChainQueryParams,
  type ColdChainResult,
} from './coldchain-engine';
import {
  queryCompliance,
  COMPLIANCE_REGISTRY,
  type ComplianceItemInfo,
  type ComplianceQueryParams,
  type ComplianceResult,
} from './compliance-engine';
import {
  queryPricing,
  PRICING_REGISTRY,
  type PricingTierInfo,
  type PricingQueryParams,
  type PricingResult,
} from './pricing-engine';
import {
  queryCareers,
  CAREERS_REGISTRY,
  type JobPostingInfo,
  type CareersQueryParams,
  type CareersResult,
} from './careers-engine';
import {
  queryCaseStudies,
  CASE_STUDIES_REGISTRY,
  type CaseStudyInfo,
  type CaseStudyMetric,
  type CaseStudyQueryParams,
  type CaseStudiesResult,
} from './casestudies-engine';
import {
  queryTestimonials,
  TESTIMONIALS_REGISTRY,
  type TestimonialItemInfo,
  type TestimonialsQueryParams,
  type TestimonialsResult,
} from './testimonials-engine';
import {
  queryTeam,
  TEAM_REGISTRY,
  type TeamMemberInfo,
  type TeamQueryParams,
  type TeamResult,
} from './team-engine';
import {
  queryFaq,
  FAQ_REGISTRY,
  type FaqItemInfo,
  type FaqQueryParams,
  type FaqResult,
} from './faq-engine';
import {
  subscribeNewsletter,
  queryNewsletterTopics,
  NEWSLETTER_TOPICS_REGISTRY,
  type NewsletterTopicInfo,
  type NewsletterSubscriptionParams,
  type NewsletterSubscriptionResult,
  type NewsletterTopicsQueryParams,
  type NewsletterTopicsResult,
} from './newsletter-engine';
import {
  queryCapabilities,
  CAPABILITIES_REGISTRY,
  type CapabilityItemInfo,
  type CapabilitiesQueryParams,
  type CapabilitiesResult,
} from './capabilities-engine';
import {
  SUPPORTED_STOREFRONT_CURRENCIES,
  getStorefrontCurrencyConfig,
  convertStorefrontPrice,
  formatStorefrontPrice,
  syncCartWithStorefrontApi,
  type StorefrontCartItem,
  updateVariantInventory,
  getVariantInventory,
  getAllVariantInventories,
  processShopifyInventoryWebhook,
  determineInventoryStatus,
  buildShopifyCustomerAuthUrl,
  authenticateWithShopifyCustomerToken,
  getStoredCustomerSession,
  clearCustomerSession,
  syncCustomerToOrchardCore,
  DEFAULT_CUSTOMER_IDENTITY,
  type ShopifyCustomerIdentity,
  type ShopifyCustomerSession,
  type CustomerSsoLoginOptions,
  type CustomerSsoAuthResult,
  type ShopifyInventoryWebhookPayload,
  type InventoryVariantRecord,
  type InventoryDeprecationEvent,
} from './storefront-cart-engine';

export interface WebMcpToolDefinition {
  name: string;
  description: string;
  inputSchema: {
    type: string;
    required?: string[];
    properties?: Record<string, { type: string; description?: string; enum?: string[] }>;
  };
  execute: (params: Record<string, unknown>) => Promise<unknown> | unknown;
}

export interface WebMcpNavigator {
  modelContext?: {
    registerTool: (tool: WebMcpToolDefinition) => void;
  };
}

export interface FreightInquiryParams {
  name?: string;
  email?: string;
  phone?: string;
  company?: string;
  serviceType?: string;
  origin?: string;
  destination?: string;
  message?: string;
}

export interface ThemeTokensQueryParams {
  theme?: 'swiss' | 'immersive' | 'freight' | 'luxury' | 'nordic' | 'active';
  includeContrast?: boolean;
  includeCss?: boolean;
}

export interface ThemeContrastQueryParams {
  theme?: 'swiss' | 'immersive' | 'freight' | 'luxury' | 'nordic' | 'all' | 'active';
}

export interface ThemeStateInfo {
  themeName: string;
  themeSlug: 'swiss' | 'immersive' | 'freight' | 'luxury' | 'nordic' | 'default';
  colorMode: 'light' | 'dark' | 'system';
  isDark: boolean;
  systemPrefersDark: boolean;
  tenantPrefix: string;
  activePage: {
    title: string;
    url: string;
    contentItemId?: string;
    contentType?: string;
  };
  renderedWidgetCount: number;
}

export interface ThemeWidgetInfo {
  widgetType: string;
  contentItemId?: string;
  id?: string;
  heading?: string;
  isInteractive: boolean;
  capabilities: string[];
}

export function detectActiveTheme(): {
  name: string;
  slug: 'swiss' | 'immersive' | 'freight' | 'luxury' | 'nordic' | 'default';
} {
  if (typeof document === 'undefined') {
    return { name: 'Swiss Editorial Theme', slug: 'swiss' };
  }

  // Check stylesheet links
  const links = Array.from(
    document.querySelectorAll<HTMLLinkElement>('link[rel="stylesheet"], link[as="style"]')
  );
  for (const link of links) {
    const href = link.href || '';
    if (href.includes('Loumars.Theme.Immersive')) {
      return { name: 'Obsidian Telemetry HUD Theme', slug: 'immersive' };
    }
    if (href.includes('Loumars.Theme.Freight')) {
      return { name: 'Aerospace Titanium Freight Theme', slug: 'freight' };
    }
    if (href.includes('Loumars.Theme.Luxury')) {
      return { name: 'Champagne Luxury Theme', slug: 'luxury' };
    }
    if (href.includes('Loumars.Theme.Nordic')) {
      return { name: 'Nordic Architectural Minimalist Theme', slug: 'nordic' };
    }
    if (href.includes('Loumars.Theme')) {
      return { name: 'Swiss Editorial Theme', slug: 'swiss' };
    }
  }

  // Check data attributes
  const bodyTheme = document.body?.getAttribute('data-theme') || '';
  const rootTheme = document.documentElement.getAttribute('data-theme') || '';
  const combined = (bodyTheme + ' ' + rootTheme).toLowerCase();
  if (combined.includes('immersive')) {
    return { name: 'Obsidian Telemetry HUD Theme', slug: 'immersive' };
  }
  if (combined.includes('freight')) {
    return { name: 'Aerospace Titanium Freight Theme', slug: 'freight' };
  }
  if (combined.includes('luxury')) {
    return { name: 'Champagne Luxury Theme', slug: 'luxury' };
  }
  if (combined.includes('nordic')) {
    return { name: 'Nordic Architectural Minimalist Theme', slug: 'nordic' };
  }

  return { name: 'Swiss Editorial Theme', slug: 'swiss' };
}

export const siteOverviewTool: WebMcpToolDefinition = {
  name: 'getSiteOverview',
  description:
    'Returns structured overview, logistics service offerings, corporate details, and public channels for Loumars CMS and enterprise logistics modules.',
  inputSchema: {
    type: 'object',
    properties: {},
  },
  execute: () => ({
    organization: 'Loumars Enterprise CMS Platform',
    tagline: 'Enterprise Multi-Tenant CMS built for .NET 10 & Global Freight Logistics',
    headquarters: 'Pilipog, Cordova, Cebu, Philippines 6017',
    contact: {
      operationsEmail: 'loucrispongasi@loumars.com',
      supportEmail: 'loucrispongasi@loumars.com',
      hotline: '+63 908 791 3380',
      hours: '24/7 Logistics & Systems Operations',
    },
    services: [
      {
        id: 'air-freight',
        name: 'International Air Freight',
        description:
          'Expedited scheduled cargo flights, aircraft chartering, temperature-controlled pharma transport across global hubs.',
      },
      {
        id: 'ocean-freight',
        name: 'Ocean Freight Forwarding',
        description:
          'Full Container Load (FCL), Less than Container Load (LCL) consolidation, and project breakbulk cargo.',
      },
      {
        id: 'customs-brokerage',
        name: 'Customs Clearance & Brokerage',
        description:
          'Accredited tariff consulting, bonded warehousing, document processing, and PEZA accredited clearance.',
      },
      {
        id: 'domestic-distribution',
        name: 'Warehousing & Domestic Distribution',
        description:
          'Secure nationwide logistics, cold-chain storage facilities, and inter-island Philippine transit.',
      },
    ],
    routes: {
      homepage: 'https://loumars.com/',
      services: 'https://loumars.com/#services',
      about: 'https://loumars.com/#about',
      contact: 'https://loumars.com/#contact',
      llmsManifest: 'https://loumars.com/llms.txt',
      sitemap: 'https://loumars.com/sitemap.xml',
    },
  }),
};

export const submitContactInquiryTool: WebMcpToolDefinition = {
  name: 'submitContactInquiry',
  description:
    'Dispatches a freight quotation request or general corporate inquiry to Loumars CMS.',
  inputSchema: {
    type: 'object',
    required: ['name', 'email', 'message'],
    properties: {
      name: { type: 'string', description: 'Sender full name or company representative' },
      email: { type: 'string', description: 'Sender contact email address' },
      phone: { type: 'string', description: 'Contact telephone or mobile number' },
      company: { type: 'string', description: 'Company name' },
      serviceType: {
        type: 'string',
        description:
          'Requested service mode (e.g. Air Freight, Ocean Freight, Customs Brokerage, Warehousing)',
      },
      origin: { type: 'string', description: 'Cargo origin city or country' },
      destination: { type: 'string', description: 'Cargo destination city or port' },
      message: { type: 'string', description: 'Detailed cargo specifications or inquiry details' },
    },
  },
  execute: (rawParams: Record<string, unknown>) => {
    const params = rawParams as FreightInquiryParams & { subject?: string };
    const contactForm =
      typeof document !== 'undefined'
        ? document.querySelector<HTMLFormElement>('form[toolname="submitContactInquiry"]')
        : null;
    if (contactForm) {
      if (params.name) {
        const el = contactForm.querySelector<HTMLInputElement>('input[name="name"]');
        if (el) el.value = params.name;
      }
      if (params.email) {
        const el = contactForm.querySelector<HTMLInputElement>('input[name="email"]');
        if (el) el.value = params.email;
      }
      if (params.phone) {
        const el = contactForm.querySelector<HTMLInputElement>('input[name="phone"]');
        if (el) el.value = params.phone;
      }
      const subjectText = params.subject || params.serviceType;
      if (subjectText) {
        const el = contactForm.querySelector<HTMLInputElement>('input[name="subject"]');
        if (el) el.value = subjectText;
      }
      if (params.message) {
        const el = contactForm.querySelector<HTMLTextAreaElement>('textarea[name="message"]');
        if (el) el.value = params.message;
      }
    }
    return {
      success: true,
      status: 'Inquiry received. Representatives will respond within 2-4 business hours.',
      payload: params,
    };
  },
};

export const searchSiteTool: WebMcpToolDefinition = {
  name: 'searchSite',
  description:
    'Searches public articles, logistics service guides, and corporate documentation across Loumars CMS.',
  inputSchema: {
    type: 'object',
    required: ['query'],
    properties: {
      query: { type: 'string', description: 'Search term or keywords' },
    },
  },
  execute: (params: Record<string, unknown>) => {
    const query = typeof params.query === 'string' ? params.query : '';
    return {
      query,
      searchUrl: `https://loumars.com/search?q=${encodeURIComponent(query)}`,
      status: 'Search query generated.',
    };
  },
};

export const trackConsignmentTool: WebMcpToolDefinition = {
  name: 'trackConsignment',
  description:
    'Tracks real-time consignment status, multimodal flight/vessel route, customs clearance, and milestone timeline for an Air Waybill (AWB), Ocean Bill of Lading, or express parcel in Loumars Global Freight logistics.',
  inputSchema: {
    type: 'object',
    properties: {
      trackingNumber: {
        type: 'string',
        description:
          'Air Waybill, Bill of Lading, or express consignment tracking number (e.g. LMR-AWB-2026-9842)',
      },
    },
  },
  execute: (params: Record<string, unknown>) => {
    const rawNumber =
      typeof params.trackingNumber === 'string'
        ? params.trackingNumber.trim()
        : typeof params.number === 'string'
          ? params.number.trim()
          : '';

    let resolvedNumber = rawNumber;
    if (!resolvedNumber && typeof document !== 'undefined') {
      const inputEl = document.querySelector<HTMLInputElement>(
        '#awb-tracking-input, [data-tracker-input]'
      );
      if (inputEl && inputEl.value.trim()) {
        resolvedNumber = inputEl.value.trim();
      }
    }

    if (!resolvedNumber) {
      resolvedNumber = 'LMR-AWB-2026-9842';
    }

    const normalizedNumber = resolvedNumber.toUpperCase();

    // Populate on-page inputs and badges if rendered
    if (typeof document !== 'undefined') {
      const inputEl = document.querySelector<HTMLInputElement>(
        '#awb-tracking-input, [data-tracker-input]'
      );
      if (inputEl) inputEl.value = normalizedNumber;

      const idBadge = document.querySelector<HTMLElement>('[data-tracker-id-badge]');
      if (idBadge) idBadge.textContent = normalizedNumber;
    }

    const isDelivered = normalizedNumber.includes('DELIV') || normalizedNumber.endsWith('-00');
    const isSea =
      normalizedNumber.includes('SEA') ||
      normalizedNumber.includes('OCEAN') ||
      normalizedNumber.includes('BOL') ||
      normalizedNumber.includes('OCN');

    const transportMode = isSea ? 'Ocean Freight Forwarding' : 'International Air Freight';
    const carrier = isSea
      ? 'Loumars Oceanic Vessel (MV Voyager / IMO 983241)'
      : 'Loumars Global Cargo Fleet (B777-F / FLT 984)';
    const originPort = isSea ? 'SGSIN' : 'MNL';
    const originName = isSea
      ? 'Port of Singapore (Jurong Terminal)'
      : 'Ninoy Aquino International Airport, Manila';
    const destPort = isSea ? 'PHCEB' : 'CEB';
    const destName = isSea
      ? 'Cebu International Port, Philippines'
      : 'Mactan-Cebu International Airport, Cebu';

    const etaStatus = isDelivered ? 'Delivered to Consignee' : 'On Schedule · ETA 14:30 PHT';
    const status = isDelivered ? 'DELIVERED' : 'IN_TRANSIT';

    if (typeof document !== 'undefined') {
      const routeEl = document.querySelector<HTMLElement>('[data-tracker-route]');
      if (routeEl) routeEl.textContent = `${originPort} (${originName}) → ${destPort} (${destName})`;

      const etaEl = document.querySelector<HTMLElement>('[data-tracker-eta]');
      if (etaEl) etaEl.textContent = etaStatus;
    }

    const now = new Date();
    const departedDate = new Date(now.getTime() - 2 * 86400000).toISOString();
    const etaDate = new Date(now.getTime() + 6 * 3600000).toISOString();

    return {
      success: true,
      trackingNumber: normalizedNumber,
      status,
      transportMode,
      carrier,
      origin: {
        port: originPort,
        name: originName,
        departedAt: departedDate,
      },
      destination: {
        port: destPort,
        name: destName,
        eta: etaDate,
      },
      etaStatus,
      packageDetails: {
        pieces: 4,
        weightKg: 1250.5,
        volumeM3: 4.8,
        commodity: isSea
          ? 'Precision Industrial Hardware & Modules'
          : 'Pharma Cold-Chain Vaccines & Biologicals',
        declaredValue: '$185,000 USD',
      },
      milestones: [
        {
          step: 1,
          title: 'Booking Confirmed & Manifest Filed',
          location: originName,
          timestamp: departedDate,
          status: 'COMPLETED',
        },
        {
          step: 2,
          title: isSea
            ? 'Container Sealed & Terminal Yard Intake'
            : 'Security Screening & Cold-Chain Intake',
          location: originPort,
          timestamp: new Date(now.getTime() - 1 * 86400000).toISOString(),
          status: 'COMPLETED',
        },
        {
          step: 3,
          title: isSea ? 'Ocean Voyage in Transit' : 'Air Transit in Progress',
          location: isSea ? 'South China Sea Transit Corridor' : 'En Route (FLT LMR-984)',
          timestamp: isDelivered
            ? new Date(now.getTime() - 10 * 3600000).toISOString()
            : new Date(now.getTime() - 3 * 3600000).toISOString(),
          status: isDelivered ? 'COMPLETED' : 'ACTIVE',
        },
        {
          step: 4,
          title: isSea
            ? 'Berth Arrival & Customs Clearance'
            : 'Customs Clearance & Ramp Inspection',
          location: destPort,
          timestamp: isDelivered ? new Date(now.getTime() - 4 * 3600000).toISOString() : null,
          status: isDelivered ? 'COMPLETED' : 'PENDING',
        },
        {
          step: 5,
          title: 'Final Consignee Delivery',
          location: 'Pilipog, Cordova, Cebu, Philippines 6017',
          timestamp: isDelivered ? now.toISOString() : null,
          status: isDelivered ? 'COMPLETED' : 'PENDING',
        },
      ],
      mode: transportMode,
      cargo: {
        weightKg: 1250.5,
        pieces: 4,
        commodity: isSea
          ? 'Precision Industrial Hardware & Modules'
          : 'Pharma Cold-Chain Vaccines & Biologicals',
      },
      apiEndpoint: `https://loumars.com/api/theme/tracking/${encodeURIComponent(normalizedNumber)}`,
      webmcpParity: true,
    };
  },
};

export const calculateEmissionsTool: WebMcpToolDefinition = {
  name: 'calculateEmissions',
  description:
    'Calculates carbon emissions, baseline footprint, sustainable fuel (SAF/Biofuel) offset, and net emissions across multimodal global freight corridors per GLEC Framework standards.',
  inputSchema: {
    type: 'object',
    properties: {
      origin: {
        type: 'string',
        description: 'Origin port/airport code (e.g. SHA, ROT, SIN, FRA, NRT, ORD, DXB, LGB)',
      },
      destination: {
        type: 'string',
        description: 'Destination port/airport code (e.g. SHA, ROT, SIN, FRA, NRT, ORD, DXB, LGB)',
      },
      mode: {
        type: 'string',
        description: 'Freight transport mode: "maritime" (ocean vessel) or "air" (cargo aircraft)',
      },
      cargoWeightTons: {
        type: 'string',
        description: 'Cargo weight in metric tons (e.g. 24)',
      },
      sustainableFuelPercent: {
        type: 'string',
        description: 'Sustainable fuel blend percentage from 0 to 100% (SAF / Biofuel, e.g. 40)',
      },
    },
  },
  execute: (params: Record<string, unknown>) => {
    let originVal = typeof params.origin === 'string' ? params.origin.trim() : undefined;
    let destVal = typeof params.destination === 'string' ? params.destination.trim() : undefined;
    let weightVal =
      typeof params.cargoWeightTons === 'number'
        ? params.cargoWeightTons
        : typeof params.cargoWeightTons === 'string'
          ? parseFloat(params.cargoWeightTons)
          : typeof params.weight === 'number'
            ? params.weight
            : typeof params.weight === 'string'
              ? parseFloat(params.weight)
              : undefined;
    let blendVal =
      typeof params.sustainableFuelPercent === 'number'
        ? params.sustainableFuelPercent
        : typeof params.sustainableFuelPercent === 'string'
          ? parseFloat(params.sustainableFuelPercent)
          : typeof params.fuelBlend === 'number'
            ? params.fuelBlend
            : typeof params.fuelBlend === 'string'
              ? parseFloat(params.fuelBlend)
              : undefined;
    const modeVal =
      params.mode === 'air' || params.mode === 'maritime' ? params.mode : undefined;

    if (typeof document !== 'undefined') {
      const originEl = document.querySelector<HTMLSelectElement | HTMLInputElement>('#carbon-origin');
      if (originEl) {
        if (originVal) {
          originEl.value = originVal.toUpperCase();
        } else if (originEl.value) {
          originVal = originEl.value;
        }
      }

      const destEl = document.querySelector<HTMLSelectElement | HTMLInputElement>('#carbon-destination');
      if (destEl) {
        if (destVal) {
          destEl.value = destVal.toUpperCase();
        } else if (destEl.value) {
          destVal = destEl.value;
        }
      }

      const weightEl = document.querySelector<HTMLInputElement>('[data-burn-weight], [data-estimator-weight]');
      if (weightEl) {
        if (weightVal !== undefined) {
          weightEl.value = String(weightVal);
        } else if (weightEl.value) {
          weightVal = parseFloat(weightEl.value);
        }
      }

      const blendEl = document.querySelector<HTMLInputElement>('[data-carbon-slider], [data-burn-saf]');
      if (blendEl) {
        if (blendVal !== undefined) {
          blendEl.value = String(blendVal);
        } else if (blendEl.value) {
          blendVal = parseFloat(blendEl.value);
        }
      }
    }

    const result = calculateEmissionsEstimate({
      origin: originVal,
      destination: destVal,
      mode: modeVal,
      cargoWeightTons: weightVal,
      sustainableFuelPercent: blendVal,
    });

    if (typeof document !== 'undefined') {
      const weightDisplay = document.querySelector<HTMLElement>('[data-burn-weight-val]');
      if (weightDisplay) {
        weightDisplay.textContent = `${result.cargoWeightTons} t (${(result.cargoWeightTons * 1000).toLocaleString()} kg)`;
      }

      const safDisplay = document.querySelector<HTMLElement>('[data-saf-percent]');
      if (safDisplay) {
        safDisplay.textContent = `${result.sustainableFuelPercent}%`;
      }

      const distDisplay = document.querySelector<HTMLElement>('[data-burn-distance]');
      if (distDisplay) {
        distDisplay.textContent = `${result.distanceNm.toLocaleString()} nm (${result.distanceKm.toLocaleString()} km)`;
      }

      const transitDisplay = document.querySelector<HTMLElement>('[data-burn-transit]');
      if (transitDisplay) {
        transitDisplay.textContent = result.transitDisplay;
      }

      const savedDisplay = document.querySelector<HTMLElement>('[data-carbon-saved], [data-burn-reduction]');
      if (savedDisplay) {
        savedDisplay.textContent = `-${result.co2ReductionTons} t CO₂`;
      }

      const netDisplay = document.querySelector<HTMLElement>('[data-net-emissions], [data-burn-net]');
      if (netDisplay) {
        netDisplay.textContent = `${result.netCo2Tons} t CO₂ Net`;
      }

      const baselineDisplay = document.querySelector<HTMLElement>('[data-burn-baseline]');
      if (baselineDisplay) {
        baselineDisplay.textContent = `${result.baselineCo2Tons} t CO₂`;
      }

      const fuelDisplay = document.querySelector<HTMLElement>('[data-burn-fuel]');
      if (fuelDisplay) {
        fuelDisplay.textContent = `${result.fuelBurnTons} t`;
      }
    }

    return {
      ...result,
      apiEndpoint: 'https://loumars.com/api/theme/emissions',
      webmcpParity: true,
    };
  },
};

export const getTradeCorridorsTool: WebMcpToolDefinition = {
  name: 'getTradeCorridors',
  description:
    'Queries real-time global trade corridors, maritime shipping lanes, transit durations, weekly TEU capacities, and air cargo routes across the Loumars ecosystem.',
  inputSchema: {
    type: 'object',
    properties: {
      laneCode: {
        type: 'string',
        description: 'Trade corridor lane code (e.g. TPX, AES, AIS, TAD, AER)',
      },
      mode: {
        type: 'string',
        description: 'Transport mode filter ("all", "maritime", "air", or "multimodal")',
      },
      region: {
        type: 'string',
        description:
          'Geographic trade corridor region (e.g. "transpacific", "asia-europe", "intra-asia", "transatlantic", "global-air")',
      },
    },
  },
  execute: (params: Record<string, unknown>) => {
    const laneCode = typeof params.laneCode === 'string' ? params.laneCode : undefined;
    const mode = typeof params.mode === 'string' ? params.mode : undefined;
    const region = typeof params.region === 'string' ? params.region : undefined;

    const result = queryTradeCorridors({ laneCode, mode, region });

    return {
      ...result,
      apiEndpoint: laneCode
        ? `https://loumars.com/api/theme/routes/${encodeURIComponent(laneCode)}`
        : 'https://loumars.com/api/theme/routes',
      webmcpParity: true,
    };
  },
};

export const getFleetSpecificationsTool: WebMcpToolDefinition = {
  name: 'getFleetSpecifications',
  description:
    'Queries real-time logistics fleet and cargo aircraft specifications, payloads, volumes, ranges, and pallet configurations across the Loumars ecosystem.',
  inputSchema: {
    type: 'object',
    properties: {
      modelCode: {
        type: 'string',
        description:
          'Fleet or aircraft model code (e.g. B777F, B747-8F, A330P2F, B767-300BCF, A321P2F, ULCV24)',
      },
      category: {
        type: 'string',
        description:
          'Fleet category filter ("all", "aircraft", "freighter-aircraft", "maritime", "maritime-vessel")',
      },
      manufacturer: {
        type: 'string',
        description: 'Manufacturer filter (e.g. "Boeing", "Airbus", "HD Hyundai")',
      },
    },
  },
  execute: (params: Record<string, unknown>) => {
    const modelCode =
      typeof params.modelCode === 'string'
        ? params.modelCode
        : typeof params.model === 'string'
          ? params.model
          : undefined;
    const category = typeof params.category === 'string' ? params.category : undefined;
    const manufacturer = typeof params.manufacturer === 'string' ? params.manufacturer : undefined;

    const result = queryFleet({ modelCode, category, manufacturer });

    return {
      ...result,
      apiEndpoint: modelCode
        ? `https://loumars.com/api/theme/fleet/${encodeURIComponent(modelCode)}`
        : 'https://loumars.com/api/theme/fleet',
      webmcpParity: true,
    };
  },
};

export const getLogisticsHubsTool: WebMcpToolDefinition = {
  name: 'getLogisticsHubs',
  description:
    'Queries global logistics hubs, deep-water maritime container ports, air cargo gateway terminals, and regional operations centers across the Loumars ecosystem.',
  inputSchema: {
    type: 'object',
    properties: {
      code: {
        type: 'string',
        description:
          'Logistics hub or gateway code (e.g. CEB, SIN, SHA, ROT, LAX, FRA, DXB, ANC, PHCEB, SGSIN, CNSHA, NLRTM, USLAX, DEFRA, AEDXB, USANC)',
      },
      type: {
        type: 'string',
        description:
          'Hub facility type filter ("all", "headquarters", "headquarters-port", "maritime", "maritime-terminal", "air", "air-cargo-gateway", "multimodal", "multimodal-hub")',
      },
      region: {
        type: 'string',
        description:
          'Geographical region filter (e.g. "Southeast Asia", "East Asia", "Europe", "North America", "Middle East")',
      },
    },
  },
  execute: (params: Record<string, unknown>) => {
    const code =
      typeof params.code === 'string'
        ? params.code
        : typeof params.hubCode === 'string'
          ? params.hubCode
          : undefined;
    const type = typeof params.type === 'string' ? params.type : undefined;
    const region = typeof params.region === 'string' ? params.region : undefined;

    const result = queryHubs({ code, type, region });

    return {
      ...result,
      apiEndpoint: code
        ? `https://loumars.com/api/theme/hubs/${encodeURIComponent(code)}`
        : 'https://loumars.com/api/theme/hubs',
      webmcpParity: true,
    };
  },
};

export const getColdChainSpecificationsTool: WebMcpToolDefinition = {
  name: 'getColdChainSpecifications',
  description:
    'Queries temperature-controlled life sciences cold-chain precision zones, packaging technology, and environmental telemetry specifications across the Loumars ecosystem.',
  inputSchema: {
    type: 'object',
    properties: {
      code: {
        type: 'string',
        description:
          'Thermal precision zone code (e.g. "CRY", "FRZ", "REF", "CRT", "WARM")',
      },
      zone: {
        type: 'string',
        description:
          'Thermal zone name or code alias (e.g. "cryogenic", "frozen", "chilled", "ambient", "incubated")',
      },
      packaging: {
        type: 'string',
        description:
          'Packaging technology filter (e.g. "Dry Ice", "Envirotainer", "VIP", "PCM", "Incubator")',
      },
      certification: {
        type: 'string',
        description:
          'Regulatory compliance or certification filter (e.g. "WHO PQS", "CEIV Pharma", "EU GDP", "USP <1079>", "ISO 13485")',
      },
    },
  },
  execute: (params: Record<string, unknown>) => {
    const code =
      typeof params.code === 'string'
        ? params.code
        : typeof params.zoneCode === 'string'
          ? params.zoneCode
          : typeof params.zone === 'string'
            ? params.zone
            : undefined;
    const packaging = typeof params.packaging === 'string' ? params.packaging : undefined;
    const certification = typeof params.certification === 'string' ? params.certification : undefined;

    const result = queryColdChain({ code, packaging, certification });

    return {
      ...result,
      apiEndpoint: code
        ? `https://loumars.com/api/theme/cold-chain/${encodeURIComponent(code)}`
        : 'https://loumars.com/api/theme/cold-chain',
      webmcpParity: true,
    };
  },
};

export const getComplianceSpecificationsTool: WebMcpToolDefinition = {
  name: 'getComplianceSpecifications',
  description:
    'Queries international customs accreditations, supply chain security certifications, environmental standards, and regulatory compliance matrix across the Loumars ecosystem.',
  inputSchema: {
    type: 'object',
    properties: {
      code: {
        type: 'string',
        description:
          'Compliance accreditation code (e.g. "AEO-F", "C-TPAT", "CEIV-PHARMA", "TAPA-TSR", "ISO-9001", "ISO-14001", "ISO-27001", "PEZA")',
      },
      certification: {
        type: 'string',
        description:
          'Certification name or standard alias (e.g. "AEO", "C-TPAT", "pharma", "TAPA", "ISO 9001", "ISO 14001", "ISO 27001", "PEZA")',
      },
      category: {
        type: 'string',
        description:
          'Regulatory category filter (e.g. "Customs & International Trade", "Supply Chain Security", "Life Sciences & Cold-Chain", "Cargo Security & Asset Protection", "Quality Management", "Environmental Sustainability & ESG", "Cybersecurity & Data Governance", "Freeport & Special Economic Zones")',
      },
      authority: {
        type: 'string',
        description:
          'Accrediting authority or standard body filter (e.g. "WCO", "CBP", "IATA", "TAPA", "ISO", "BOC", "PEZA")',
      },
    },
  },
  execute: (params: Record<string, unknown>) => {
    const code =
      typeof params.code === 'string'
        ? params.code
        : typeof params.certification === 'string'
          ? params.certification
          : typeof params.standard === 'string'
            ? params.standard
            : undefined;
    const category = typeof params.category === 'string' ? params.category : undefined;
    const authority = typeof params.authority === 'string' ? params.authority : undefined;

    const result = queryCompliance({ code, category, authority });

    return {
      ...result,
      apiEndpoint: code
        ? `https://loumars.com/api/theme/compliance/${encodeURIComponent(code)}`
        : 'https://loumars.com/api/theme/compliance',
      webmcpParity: true,
    };
  },
};

export const getPricingPlansTool: WebMcpToolDefinition = {
  name: 'getPricingPlans',
  description:
    'Queries commercial freight pricing tiers, rate cards, and service-level agreement (SLA) commitments across the Loumars ecosystem.',
  inputSchema: {
    type: 'object',
    properties: {
      code: {
        type: 'string',
        description:
          'Pricing tier code (e.g. "ECONOMY_LCL", "STANDARD_CARGO", "EXPRESS_AIR", "COLD_CHAIN_PHARMA", "AEROSPACE_CHARTER", "SANCTUARY_RETREAT")',
      },
      tier: {
        type: 'string',
        description:
          'Pricing tier code or name alias (e.g. "economy", "standard", "express", "pharma", "charter", "sanctuary")',
      },
      category: {
        type: 'string',
        description:
          'Pricing category filter (e.g. "Ocean & Ground Logistics", "Multimodal Air/Ocean Cargo", "Priority Air Cargo", "Life Sciences & Specialized", "Aerospace & Dedicated Charter", "Luxury Hospitality & Concierge")',
      },
      transportMode: {
        type: 'string',
        description:
          'Transport mode filter (e.g. "Ocean/Ground", "Multimodal", "Air Express", "Active Cold-Chain", "Air Charter", "Concierge")',
      },
    },
  },
  execute: (params: Record<string, unknown>) => {
    const code =
      typeof params.code === 'string'
        ? params.code
        : typeof params.tier === 'string'
          ? params.tier
          : typeof params.plan === 'string'
            ? params.plan
            : undefined;
    const category = typeof params.category === 'string' ? params.category : undefined;
    const transportMode = typeof params.transportMode === 'string' ? params.transportMode : undefined;

    const result = queryPricing({ code, category, transportMode });

    return {
      ...result,
      apiEndpoint: code
        ? `https://loumars.com/api/theme/pricing/${encodeURIComponent(code)}`
        : 'https://loumars.com/api/theme/pricing',
      webmcpParity: true,
    };
  },
};

export const getJobPostingsTool: WebMcpToolDefinition = {
  name: 'getJobPostings',
  description:
    'Queries open career positions, engineering requisitions, flight ops positions, and luxury concierge roles across the Loumars ecosystem.',
  inputSchema: {
    type: 'object',
    properties: {
      id: {
        type: 'string',
        description:
          'Requisition or job identifier (e.g. "CAREER-AV-001", "CAREER-LOG-002", "CAREER-ARCH-003", "CAREER-MAR-004", "CAREER-SEC-005", "CAREER-HOSP-006")',
      },
      jobId: {
        type: 'string',
        description: 'Alias for requisition id or job title search',
      },
      department: {
        type: 'string',
        description:
          'Department filter (e.g. "Engineering & Flight Systems", "Supply Chain & Logistics", "Design & Architecture", "Maritime Logistics", "Security & Compliance", "Luxury Hospitality")',
      },
      employmentType: {
        type: 'string',
        description: 'Employment type filter (e.g. "Full-time", "Contract", "Rotational")',
      },
      location: {
        type: 'string',
        description:
          'Location filter (e.g. "Cebu", "Singapore", "Oslo", "Geneva", "Rotterdam", "Lucerne", "Remote")',
      },
      isRemote: {
        type: 'boolean',
        description: 'Optional boolean filter for remote-eligible roles',
      },
    },
  },
  execute: (params: Record<string, unknown>) => {
    const id =
      typeof params.id === 'string'
        ? params.id
        : typeof params.jobId === 'string'
          ? params.jobId
          : typeof params.code === 'string'
            ? params.code
            : undefined;
    const department = typeof params.department === 'string' ? params.department : undefined;
    const employmentType =
      typeof params.employmentType === 'string' ? params.employmentType : undefined;
    const location = typeof params.location === 'string' ? params.location : undefined;
    const isRemote = typeof params.isRemote === 'boolean' ? params.isRemote : undefined;

    const result = queryCareers({ id, department, employmentType, location, isRemote });

    return {
      ...result,
      apiEndpoint: id
        ? `https://loumars.com/api/theme/careers/${encodeURIComponent(id)}`
        : 'https://loumars.com/api/theme/careers',
      webmcpParity: true,
    };
  },
};

export const getCaseStudiesTool: WebMcpToolDefinition = {
  name: 'getCaseStudies',
  description:
    'Queries engineering case studies, incident post-mortems, constraint-solution pairs, and quantified ROI performance metrics across the Loumars ecosystem.',
  inputSchema: {
    type: 'object',
    properties: {
      id: {
        type: 'string',
        description:
          'Case study or system identifier (e.g. "CASE-SYS-001", "CASE-COLD-002", "CASE-ARCH-003", "CASE-MAR-004", "CASE-SEC-005", "CASE-LUX-006")',
      },
      studyId: {
        type: 'string',
        description: 'Alias for case study or system identifier',
      },
      industry: {
        type: 'string',
        description:
          'Industry filter (e.g. "Aerospace & Aviation", "Global Cold-Chain", "Architectural Engineering", "Maritime Freight", "Autonomous AI & Cyber", "Ultra-Luxury Hospitality")',
      },
      theme: {
        type: 'string',
        description: 'Theme filter (e.g. "swiss", "immersive", "freight", "luxury", "nordic")',
      },
      statusFlag: {
        type: 'string',
        description:
          'Verification status flag filter (e.g. "PRODUCTION RESILIENCE VERIFIED", "IATA CEIV AUDIT CERTIFIED", "BREEAM OUTSTANDING VERIFIED", "IMO MEPC-80 DECARBONIZATION TARGET MET", "SOC2 TYPE II & ISO 27001 AUDIT GREEN", "ULTRA-HNW PRIVACY ACCREDITATION")',
      },
    },
  },
  execute: (params: Record<string, unknown>) => {
    const id =
      typeof params.id === 'string'
        ? params.id
        : typeof params.studyId === 'string'
          ? params.studyId
          : typeof params.code === 'string'
            ? params.code
            : undefined;
    const industry = typeof params.industry === 'string' ? params.industry : undefined;
    const theme = typeof params.theme === 'string' ? params.theme : undefined;
    const statusFlag = typeof params.statusFlag === 'string' ? params.statusFlag : undefined;

    const result = queryCaseStudies({ id, industry, theme, statusFlag });

    return {
      ...result,
      apiEndpoint: id
        ? `https://loumars.com/api/theme/case-studies/${encodeURIComponent(id)}`
        : 'https://loumars.com/api/theme/case-studies',
      webmcpParity: true,
    };
  },
};

export const getTestimonialsTool: WebMcpToolDefinition = {
  name: 'getTestimonials',
  description:
    'Queries verified client testimonials, customer reviews, executive endorsements, and audit ratings across the Loumars ecosystem.',
  inputSchema: {
    type: 'object',
    properties: {
      id: {
        type: 'string',
        description:
          'Testimonial or review identifier (e.g. "TEST-AV-001", "TEST-COLD-002", "TEST-ARCH-003", "TEST-HUD-004", "TEST-LUX-005", "TEST-SYS-006")',
      },
      testimonialId: {
        type: 'string',
        description: 'Alias for testimonial identifier',
      },
      industry: {
        type: 'string',
        description:
          'Industry filter (e.g. "Aerospace Freight", "Healthcare Logistics", "Architectural Engineering", "Telemetry & Avionics", "Ultra-Luxury Hospitality", "Autonomous AI & Cyber")',
      },
      theme: {
        type: 'string',
        description: 'Theme filter (e.g. "swiss", "immersive", "freight", "luxury", "nordic")',
      },
      minRating: {
        type: 'string',
        description: 'Minimum rating filter (1-5)',
      },
    },
  },
  execute: (params: Record<string, unknown>) => {
    const id =
      typeof params.id === 'string'
        ? params.id
        : typeof params.testimonialId === 'string'
          ? params.testimonialId
          : typeof params.code === 'string'
            ? params.code
            : undefined;
    const industry = typeof params.industry === 'string' ? params.industry : undefined;
    const theme = typeof params.theme === 'string' ? params.theme : undefined;
    const minRating =
      typeof params.minRating === 'number'
        ? params.minRating
        : typeof params.minRating === 'string'
          ? parseInt(params.minRating, 10)
          : undefined;

    const result = queryTestimonials({ id, industry, theme, minRating });

    return {
      ...result,
      apiEndpoint: id
        ? `https://loumars.com/api/theme/testimonials/${encodeURIComponent(id)}`
        : 'https://loumars.com/api/theme/testimonials',
      webmcpParity: true,
    };
  },
};

export const getTeamMembersTool: WebMcpToolDefinition = {
  name: 'getTeamMembers',
  description:
    'Queries corporate executive officers, department directors, and specialist personnel roster across the Loumars ecosystem.',
  inputSchema: {
    type: 'object',
    properties: {
      id: {
        type: 'string',
        description:
          'Team member identifier, name, or initials (e.g. "TEAM-EXEC-001", "TEAM-OPS-002", "TEAM-TECH-003", "TEAM-ARCH-004", "TEAM-LUX-005", "TEAM-QA-006", "AM", "Alexandre Mercer")',
      },
      memberId: {
        type: 'string',
        description: 'Alias for team member identifier',
      },
      department: {
        type: 'string',
        description:
          'Department filter (e.g. "Executive Leadership", "Global Operations", "Autonomous Systems & Avionics", "Architectural Engineering", "Luxury Hospitality & Private Estates", "Cold-Chain & Life Sciences")',
      },
      theme: {
        type: 'string',
        description: 'Theme filter (e.g. "swiss", "immersive", "freight", "luxury", "nordic")',
      },
      executiveOnly: {
        type: 'boolean',
        description: 'Filter only executive-tier leadership officers (true/false)',
      },
    },
  },
  execute: (params: Record<string, unknown>) => {
    const id =
      typeof params.id === 'string'
        ? params.id
        : typeof params.memberId === 'string'
          ? params.memberId
          : typeof params.code === 'string'
            ? params.code
            : undefined;
    const department = typeof params.department === 'string' ? params.department : undefined;
    const theme = typeof params.theme === 'string' ? params.theme : undefined;
    const executiveOnly =
      typeof params.executiveOnly === 'boolean'
        ? params.executiveOnly
        : typeof params.executiveOnly === 'string'
          ? params.executiveOnly.toLowerCase() === 'true'
          : undefined;

    const result = queryTeam({ id, department, theme, executiveOnly });

    return {
      ...result,
      apiEndpoint: id
        ? `https://loumars.com/api/theme/team/${encodeURIComponent(id)}`
        : 'https://loumars.com/api/theme/team',
      webmcpParity: true,
    };
  },
};

export const getFaqTool: WebMcpToolDefinition = {
  name: 'getFaq',
  description:
    'Queries corporate frequently asked questions, regulatory disclosures, customs clearance guidelines, and knowledge base specifications across the Loumars Quad-Theme Ecosystem supporting category, theme, keyword search, and individual FAQ ID lookups.',
  inputSchema: {
    type: 'object',
    properties: {
      id: {
        type: 'string',
        description:
          'FAQ identifier, keyword, or badge (e.g. "FAQ-LOG-001", "FAQ-CRYO-002", "FAQ-HUD-003", "FAQ-ARCH-004", "FAQ-LUX-005", "FAQ-CUST-006", "GLOBAL LOGISTICS", "COLD-CHAIN COMPLIANCE")',
      },
      faqId: {
        type: 'string',
        description: 'Alias for FAQ identifier',
      },
      category: {
        type: 'string',
        description:
          'Category filter (e.g. "Global Logistics & Freight", "Cold-Chain & Life Sciences", "Autonomous Systems & HUD Telemetry", "Scandinavian Architectural Engineering", "Luxury Hospitality & Private Estates", "Customs & Regulatory Compliance")',
      },
      theme: {
        type: 'string',
        description: 'Theme filter (e.g. "swiss", "immersive", "freight", "luxury", "nordic")',
      },
      query: {
        type: 'string',
        description: 'Keyword search query across FAQ questions, answers, and tags',
      },
    },
  },
  execute: (params: Record<string, unknown>) => {
    const id =
      typeof params.id === 'string'
        ? params.id
        : typeof params.faqId === 'string'
          ? params.faqId
          : typeof params.code === 'string'
            ? params.code
            : undefined;
    const category = typeof params.category === 'string' ? params.category : undefined;
    const theme = typeof params.theme === 'string' ? params.theme : undefined;
    const query = typeof params.query === 'string' ? params.query : undefined;

    const result = queryFaq({ id, category, theme, query });

    return {
      ...result,
      apiEndpoint: id
        ? `https://loumars.com/api/theme/faq/${encodeURIComponent(id)}`
        : 'https://loumars.com/api/theme/faq',
      webmcpParity: true,
    };
  },
};

export const subscribeNewsletterTool: WebMcpToolDefinition = {
  name: 'subscribeNewsletter',
  description:
    'Subscribe an email address to receive supply chain insights, tariff changes, and company announcements across Loumars Quad-Theme Ecosystem.',
  inputSchema: {
    type: 'object',
    required: ['email'],
    properties: {
      email: {
        type: 'string',
        description: 'Subscriber email address to receive intelligence updates',
      },
      name: {
        type: 'string',
        description: 'Optional subscriber full name or representative title',
      },
      frequency: {
        type: 'string',
        description: 'Preferred publication frequency (e.g. Weekly, Monthly, Quarterly)',
      },
      theme: {
        type: 'string',
        description: 'Theme preference (swiss, immersive, freight, luxury, nordic, all)',
      },
    },
  },
  execute: (rawParams: Record<string, unknown>) => {
    const email = typeof rawParams.email === 'string' ? rawParams.email : '';
    const name = typeof rawParams.name === 'string' ? rawParams.name : undefined;
    const frequency = typeof rawParams.frequency === 'string' ? rawParams.frequency : undefined;
    const theme = typeof rawParams.theme === 'string' ? rawParams.theme : undefined;
    const topics = Array.isArray(rawParams.topics) ? (rawParams.topics as string[]) : undefined;

    const newsletterForm =
      typeof document !== 'undefined'
        ? (document.querySelector<HTMLFormElement>('form[toolname="subscribeNewsletter"]') ||
           (document.getElementById('acms-newsletter-form') as HTMLFormElement) ||
           (document.getElementById('immersive-newsletter-form') as HTMLFormElement))
        : null;

    if (newsletterForm && email) {
      const emailInput = newsletterForm.querySelector<HTMLInputElement>('input[type="email"]');
      if (emailInput) emailInput.value = email;
    }

    try {
      const result = subscribeNewsletter({
        email,
        name,
        frequency,
        theme,
        topics,
      });
      return {
        ...result,
        apiEndpoint: 'https://loumars.com/api/theme/newsletter',
        webmcpParity: true,
      };
    } catch (err: any) {
      return {
        success: false,
        error: err?.message || 'Subscription failed',
      };
    }
  },
};

export const getNewsletterTopicsTool: WebMcpToolDefinition = {
  name: 'getNewsletterTopics',
  description:
    'Query available corporate newsletter channels, publication frequencies, and advisory tracks across Loumars Quad-Theme Ecosystem.',
  inputSchema: {
    type: 'object',
    properties: {
      id: {
        type: 'string',
        description:
          'Newsletter topic identifier, keyword, or badge (e.g. "TOPIC-SUPPLY-CHAIN", "TOPIC-COLD-CHAIN", "TOPIC-AVIONICS-HUD", "TOPIC-NORDIC-ARCH", "TOPIC-LUXURY-FOLIO", "TOPIC-REGULATORY-ALERTS", "FREIGHT DISPATCH")',
      },
      category: {
        type: 'string',
        description:
          'Category filter (e.g. "Global Freight & Supply Chain", "Cold-Chain & Life Sciences", "Autonomous Systems & HUD Telemetry", "Scandinavian Architectural Engineering", "Luxury Hospitality & Private Estates", "Customs & Regulatory Compliance")',
      },
      theme: {
        type: 'string',
        description: 'Theme filter (e.g. "swiss", "immersive", "freight", "luxury", "nordic")',
      },
      frequency: {
        type: 'string',
        description:
          'Publication frequency (e.g. "Weekly", "Monthly", "Quarterly", "Bi-Monthly", "Seasonal", "Ad-Hoc / Real-Time Alert")',
      },
      query: {
        type: 'string',
        description: 'Keyword search query across topic titles, descriptions, and tags',
      },
    },
  },
  execute: (params: Record<string, unknown>) => {
    const id =
      typeof params.id === 'string'
        ? params.id
        : typeof params.topicId === 'string'
          ? params.topicId
          : typeof params.code === 'string'
            ? params.code
            : undefined;
    const category = typeof params.category === 'string' ? params.category : undefined;
    const theme = typeof params.theme === 'string' ? params.theme : undefined;
    const frequency = typeof params.frequency === 'string' ? params.frequency : undefined;
    const query = typeof params.query === 'string' ? params.query : undefined;

    const result = queryNewsletterTopics({ id, category, theme, frequency, query });

    return {
      ...result,
      apiEndpoint: id
        ? `https://loumars.com/api/theme/newsletter/topics/${encodeURIComponent(id)}`
        : 'https://loumars.com/api/theme/newsletter/topics',
      webmcpParity: true,
    };
  },
};

export const getCapabilitiesTool: WebMcpToolDefinition = {
  name: 'getCapabilities',
  description:
    'Query multimodal capabilities, enterprise service matrices, and operational strengths across Loumars Quad-Theme Ecosystem.',
  inputSchema: {
    type: 'object',
    properties: {
      id: {
        type: 'string',
        description:
          'Capability identifier, keyword, or badge (e.g. "CAP-AIR-001", "CAP-MAR-002", "CAP-CRYO-003", "CAP-ARCH-004", "CAP-LUX-005", "CAP-SEC-006", "FLAGSHIP AIR")',
      },
      category: {
        type: 'string',
        description:
          'Category filter (e.g. "Aviation & Air Freight", "Ocean Freight & Intermodal", "Telemetry & Precision Monitoring", "Architectural & Structural Engineering", "Luxury Hospitality & Private Aviation", "Compliance, Customs & Regulatory Affairs")',
      },
      theme: {
        type: 'string',
        description: 'Theme filter (e.g. "swiss", "immersive", "freight", "luxury", "nordic")',
      },
      tier: {
        type: 'string',
        description: 'Service tier filter (e.g. "Tier 1 Flagship", "Tier 2 Advanced")',
      },
      search: {
        type: 'string',
        description: 'Search query across capability titles, descriptions, metrics, certifications, and tags',
      },
    },
  },
  execute: (params: Record<string, unknown>) => {
    const id =
      typeof params.id === 'string'
        ? params.id
        : typeof params.capabilityId === 'string'
          ? params.capabilityId
          : typeof params.code === 'string'
            ? params.code
            : undefined;
    const category = typeof params.category === 'string' ? params.category : undefined;
    const theme = typeof params.theme === 'string' ? params.theme : undefined;
    const tier = typeof params.tier === 'string' ? params.tier : undefined;
    const search =
      typeof params.search === 'string'
        ? params.search
        : typeof params.query === 'string'
          ? params.query
          : undefined;

    const result = queryCapabilities({ id, category, theme, tier, search });

    return {
      ...result,
      apiEndpoint: id
        ? `https://loumars.com/api/theme/capabilities/${encodeURIComponent(id)}`
        : 'https://loumars.com/api/theme/capabilities',
      webmcpParity: true,
    };
  },
};

export const getShopifyCartTool: WebMcpToolDefinition = {
  name: 'getShopifyCart',
  description:
    'Query the active Shopify theme cart, item count, multi-currency subtotal, and Storefront API sync state.',
  inputSchema: {
    type: 'object',
    properties: {
      currency: {
        type: 'string',
        description: 'Optional currency override (e.g. USD, EUR, GBP, CHF, JPY, CAD, AUD, SEK, NOK, DKK)',
      },
    },
  },
  execute: (params: Record<string, unknown>) => {
    let curr = typeof params.currency === 'string' ? params.currency : 'USD';
    if (typeof window !== 'undefined') {
      const w = window as unknown as { afGetCartCurrency?: () => string };
      if (typeof w.afGetCartCurrency === 'function') {
        curr = typeof params.currency === 'string' ? params.currency : w.afGetCartCurrency();
      } else if (!params.currency) {
        try {
          curr = localStorage.getItem('loumars_cart_currency') || 'USD';
        } catch {
          curr = 'USD';
        }
      }
    }
    const config = getStorefrontCurrencyConfig(curr);
    let items: StorefrontCartItem[] = [];
    if (typeof window !== 'undefined') {
      try {
        items = JSON.parse(localStorage.getItem('loumars_cart') || '[]') as StorefrontCartItem[];
      } catch {
        items = [];
      }
    }
    let itemCount = 0;
    let rawSubtotalUsd = 0;
    for (const it of items) {
      const qty = it.quantity || 1;
      itemCount += qty;
      rawSubtotalUsd += (it.price || 0) * qty;
    }
    const convertedSubtotal = convertStorefrontPrice(rawSubtotalUsd, config.code);
    return {
      success: true,
      currency: config.code,
      currencySymbol: config.symbol,
      itemCount,
      subtotalAmount: convertedSubtotal,
      formattedSubtotal: formatStorefrontPrice(convertedSubtotal, config.code),
      supportedCurrencies: Object.keys(SUPPORTED_STOREFRONT_CURRENCIES),
      items,
    };
  },
};

export const setCartCurrencyTool: WebMcpToolDefinition = {
  name: 'setCartCurrency',
  description:
    'Switch active currency for the shopping cart and recalculate localized Storefront prices across all theme drawers.',
  inputSchema: {
    type: 'object',
    required: ['currency'],
    properties: {
      currency: {
        type: 'string',
        description: 'Target currency code (USD, EUR, GBP, CHF, JPY, CAD, AUD, SEK, NOK, DKK)',
      },
    },
  },
  execute: (params: Record<string, unknown>) => {
    const targetCurrency = typeof params.currency === 'string' ? params.currency : 'USD';
    const config = getStorefrontCurrencyConfig(targetCurrency);
    if (typeof window !== 'undefined') {
      const w = window as unknown as { afSetCartCurrency?: (c: string) => string };
      if (typeof w.afSetCartCurrency === 'function') {
        w.afSetCartCurrency(config.code);
      } else {
        try {
          localStorage.setItem('loumars_cart_currency', config.code);
        } catch {
          // ignore
        }
      }
    }
    return {
      success: true,
      currency: config.code,
      symbol: config.symbol,
      rate: config.rate,
      locale: config.locale,
    };
  },
};

export const syncShopifyCartTool: WebMcpToolDefinition = {
  name: 'syncShopifyCart',
  description:
    'Synchronize local shopping cart lines with Shopify Storefront API GraphQL mutations (cartCreate/cartBuyerIdentityUpdate).',
  inputSchema: {
    type: 'object',
    properties: {
      currency: {
        type: 'string',
        description: 'Optional currency code override for checkout buyer identity',
      },
      endpoint: {
        type: 'string',
        description: 'Custom Shopify GraphQL endpoint override',
      },
      storefrontAccessToken: {
        type: 'string',
        description: 'Custom Shopify Storefront Access Token override',
      },
    },
  },
  execute: async (params: Record<string, unknown>) => {
    const currency = typeof params.currency === 'string' ? params.currency : undefined;
    if (typeof window !== 'undefined') {
      const w = window as unknown as {
        afSyncStorefrontCart?: (c?: string) => Promise<unknown>;
      };
      if (typeof w.afSyncStorefrontCart === 'function') {
        return await w.afSyncStorefrontCart(currency);
      }
    }
    let items: StorefrontCartItem[] = [];
    if (typeof window !== 'undefined') {
      try {
        items = JSON.parse(localStorage.getItem('loumars_cart') || '[]') as StorefrontCartItem[];
      } catch {
        items = [];
      }
    }
    const endpoint = typeof params.endpoint === 'string' ? params.endpoint : undefined;
    const token =
      typeof params.storefrontAccessToken === 'string'
        ? params.storefrontAccessToken
        : undefined;
    return await syncCartWithStorefrontApi(items, {
      currencyCode: currency,
      endpoint,
      storefrontAccessToken: token,
    });
  },
};

export const getInventoryLevelsTool: WebMcpToolDefinition = {
  name: 'getInventoryLevels',
  description:
    'Retrieves cached or active real-time inventory level records and stock statuses for Shopify product variants.',
  inputSchema: {
    type: 'object',
    properties: {
      variantId: {
        type: 'string',
        description: 'Optional variant ID (GID or numeric ID) to query a specific inventory record',
      },
    },
  },
  execute: (params: Record<string, unknown>) => {
    const variantId = typeof params.variantId === 'string' ? params.variantId : undefined;
    if (variantId) {
      const record = getVariantInventory(variantId);
      return {
        success: true,
        variantId,
        record: record ?? null,
      };
    }
    const all = getAllVariantInventories();
    return {
      success: true,
      totalVariants: Object.keys(all).length,
      inventories: all,
    };
  },
};

export const processInventoryWebhookTool: WebMcpToolDefinition = {
  name: 'processInventoryWebhook',
  description:
    'Process an incoming Shopify inventory_levels/update webhook payload and broadcast deprecation events across headless embeds.',
  inputSchema: {
    type: 'object',
    required: ['inventory_item_id', 'available'],
    properties: {
      inventory_item_id: {
        type: 'string',
        description: 'Shopify inventory item ID or GID',
      },
      available: {
        type: 'number',
        description: 'Available inventory quantity level',
      },
      location_id: {
        type: 'string',
        description: 'Optional Shopify inventory location ID',
      },
      variant_id: {
        type: 'string',
        description: 'Optional associated variant ID',
      },
      updated_at: {
        type: 'string',
        description: 'Optional ISO timestamp of the update',
      },
    },
  },
  execute: (params: Record<string, unknown>) => {
    const payload: ShopifyInventoryWebhookPayload = {
      inventory_item_id: String(params.inventory_item_id ?? ''),
      location_id: params.location_id ? String(params.location_id) : undefined,
      available:
        typeof params.available === 'number'
          ? params.available
          : Number(params.available ?? 0),
      variant_id: params.variant_id ? String(params.variant_id) : undefined,
      updated_at: typeof params.updated_at === 'string' ? params.updated_at : undefined,
    };
    const event = processShopifyInventoryWebhook(payload);
    return {
      success: true,
      event,
    };
  },
};

export const deprecateInventoryLevelTool: WebMcpToolDefinition = {
  name: 'deprecateInventoryLevel',
  description:
    'Explicitly update or deprecate a product variant inventory level, updating in-memory cache and dispatching DOM events.',
  inputSchema: {
    type: 'object',
    required: ['variantId', 'available'],
    properties: {
      variantId: {
        type: 'string',
        description: 'Variant identifier (GID or numeric ID)',
      },
      available: {
        type: 'number',
        description: 'New available quantity level (0 for out-of-stock deprecation)',
      },
      inventoryItemId: {
        type: 'string',
        description: 'Optional associated inventory item identifier',
      },
      locationId: {
        type: 'string',
        description: 'Optional associated location identifier',
      },
    },
  },
  execute: (params: Record<string, unknown>) => {
    const variantId = String(params.variantId ?? '');
    const available =
      typeof params.available === 'number'
        ? params.available
        : Number(params.available ?? 0);
    const locationId = params.locationId ? String(params.locationId) : undefined;

    const event = updateVariantInventory(variantId, available, { locationId });
    return {
      success: true,
      event,
    };
  },
};

export const getCustomerSessionTool: WebMcpToolDefinition = {
  name: 'getCustomerSession',
  description:
    'Retrieves the active authenticated Shopify customer account session and claims.',
  inputSchema: {
    type: 'object',
    properties: {
      checkOrchardSync: {
        type: 'boolean',
        description: 'Optional flag to verify synchronization with Orchard Core identity',
      },
    },
  },
  execute: () => {
    const session = getStoredCustomerSession();
    return {
      success: true,
      isAuthenticated: !!session && session.isAuthenticated,
      session: session ?? null,
    };
  },
};

export const loginCustomerSsoTool: WebMcpToolDefinition = {
  name: 'loginCustomerSso',
  description:
    'Initiates or simulates Shopify Customer Accounts API single sign-on authentication.',
  inputSchema: {
    type: 'object',
    properties: {
      shopDomain: {
        type: 'string',
        description: 'Optional shop domain (e.g. storefront.loumars.com)',
      },
      clientId: {
        type: 'string',
        description: 'Optional client ID for Customer Accounts OAuth',
      },
      redirectUri: {
        type: 'string',
        description: 'Optional redirect callback URI',
      },
      simulateAuth: {
        type: 'boolean',
        description: 'When true, immediately authenticates with a valid mock customer identity',
      },
      accessToken: {
        type: 'string',
        description: 'Optional custom access token for simulated authentication',
      },
      email: {
        type: 'string',
        description: 'Optional email address for the customer identity',
      },
      displayName: {
        type: 'string',
        description: 'Optional display name for the customer identity',
      },
    },
  },
  execute: (params: Record<string, unknown>) => {
    const simulate = params.simulateAuth !== false;
    const authUrl = buildShopifyCustomerAuthUrl({
      shopDomain: typeof params.shopDomain === 'string' ? params.shopDomain : undefined,
      clientId: typeof params.clientId === 'string' ? params.clientId : undefined,
      redirectUri: typeof params.redirectUri === 'string' ? params.redirectUri : undefined,
    });

    if (simulate) {
      const token = typeof params.accessToken === 'string' ? params.accessToken : `shpca_${Date.now()}_tok`;
      const customer: Partial<ShopifyCustomerIdentity> = {};
      if (typeof params.email === 'string') customer.email = params.email;
      if (typeof params.displayName === 'string') customer.displayName = params.displayName;
      if (typeof params.firstName === 'string') customer.firstName = params.firstName;
      if (typeof params.lastName === 'string') customer.lastName = params.lastName;
      const session = authenticateWithShopifyCustomerToken({
        accessToken: token,
        customer: Object.keys(customer).length > 0 ? customer : undefined,
      });
      return {
        success: true,
        session,
        authUrl,
      };
    }

    return {
      success: true,
      authUrl,
    };
  },
};

export const logoutCustomerSsoTool: WebMcpToolDefinition = {
  name: 'logoutCustomerSso',
  description: 'Terminates the active Shopify customer account session.',
  inputSchema: {
    type: 'object',
    properties: {},
  },
  execute: () => {
    clearCustomerSession();
    return {
      success: true,
      message: 'Customer session terminated.',
    };
  },
};

export const getThemeStateTool: WebMcpToolDefinition = {
  name: 'getThemeState',
  description:
    'Retrieves runtime state of the active Loumars theme including design system tokens, color mode (dark/light/system), system preferences, tenant prefix, and active page metadata.',
  inputSchema: {
    type: 'object',
    properties: {},
  },
  execute: (): ThemeStateInfo => {
    const theme = detectActiveTheme();
    const root = typeof document !== 'undefined' ? document.documentElement : null;
    const isDark = root ? root.classList.contains('dark') : false;

    let colorMode: 'light' | 'dark' | 'system' = 'system';
    if (typeof window !== 'undefined' && typeof window.afGetThemeMode === 'function') {
      colorMode = window.afGetThemeMode();
    } else if (root) {
      const modeAttr = root.getAttribute('data-theme-mode');
      if (modeAttr === 'dark' || modeAttr === 'light' || modeAttr === 'system') {
        colorMode = modeAttr;
      } else {
        try {
          const stored = localStorage.getItem('loumars-theme-mode');
          if (stored === 'dark' || stored === 'light' || stored === 'system') {
            colorMode = stored;
          }
        } catch {}
      }
    }

    const systemPrefersDark =
      typeof window !== 'undefined' &&
      Boolean(window.matchMedia && window.matchMedia('(prefers-color-scheme: dark)').matches);

    const body = typeof document !== 'undefined' ? document.body : null;
    const tenantPrefix = body?.getAttribute('data-tenant-prefix') || '';
    const mainEl = typeof document !== 'undefined' ? document.querySelector('main') : null;

    const widgetCount =
      typeof document !== 'undefined'
        ? document.querySelectorAll('[data-widget-type], [data-content-item-id]:not(body)').length
        : 0;

    return {
      themeName: theme.name,
      themeSlug: theme.slug,
      colorMode,
      isDark,
      systemPrefersDark,
      tenantPrefix,
      activePage: {
        title: typeof document !== 'undefined' ? document.title : '',
        url: typeof window !== 'undefined' ? window.location.href : '',
        contentItemId:
          body?.getAttribute('data-content-item-id') ||
          mainEl?.getAttribute('data-content-item-id') ||
          undefined,
        contentType:
          body?.getAttribute('data-content-type') ||
          mainEl?.getAttribute('data-content-type') ||
          undefined,
      },
      renderedWidgetCount: widgetCount,
    };
  },
};

export const setThemeModeTool: WebMcpToolDefinition = {
  name: 'setThemeMode',
  description:
    'Updates active theme color mode to light, dark, or system, synchronizing root document classes and switch controls.',
  inputSchema: {
    type: 'object',
    required: ['mode'],
    properties: {
      mode: {
        type: 'string',
        enum: ['light', 'dark', 'system'],
        description: 'Desired color mode: "light", "dark", or "system"',
      },
    },
  },
  execute: (params: Record<string, unknown>) => {
    const rawMode = params.mode;
    const mode =
      rawMode === 'dark' || rawMode === 'light' || rawMode === 'system' ? rawMode : 'system';

    if (typeof window !== 'undefined' && typeof window.afSetThemeMode === 'function') {
      window.afSetThemeMode(mode);
    } else if (typeof document !== 'undefined') {
      const root = document.documentElement;
      let isDark = false;
      if (mode === 'dark') {
        isDark = true;
      } else if (mode === 'light') {
        isDark = false;
      } else {
        isDark = Boolean(
          window.matchMedia && window.matchMedia('(prefers-color-scheme: dark)').matches
        );
      }

      if (isDark) {
        root.classList.add('dark');
        root.classList.remove('light');
        if (document.body) {
          document.body.classList.add('dark');
          document.body.classList.remove('light');
        }
      } else {
        root.classList.add('light');
        root.classList.remove('dark');
        if (document.body) {
          document.body.classList.add('light');
          document.body.classList.remove('dark');
        }
      }
      root.setAttribute('data-theme-mode', mode);
      if (document.body) {
        document.body.setAttribute('data-theme-mode', mode);
      }
      try {
        localStorage.setItem('loumars-theme-mode', mode);
      } catch {}
    }

    const isDark =
      typeof document !== 'undefined' ? document.documentElement.classList.contains('dark') : false;
    return {
      success: true,
      mode,
      isDark,
      message: `Theme color mode successfully updated to ${mode}.`,
    };
  },
};

export const getThemeTokensTool: WebMcpToolDefinition = {
  name: 'getThemeTokens',
  description:
    'Retrieves strongly typed design system tokens, color atmospheres (light/dark surfaces, borders, text), WCAG 2.1 AA/AAA contrast reports, typography font stacks, radii, and Tailwind CSS v4 @theme CSS variable definitions for any of the 5 themes (swiss, immersive, freight, luxury, nordic) or the currently active theme.',
  inputSchema: {
    type: 'object',
    properties: {
      theme: {
        type: 'string',
        enum: ['swiss', 'immersive', 'freight', 'luxury', 'nordic', 'active'],
        description: 'Target theme slug to query, or "active" to query the active theme',
      },
      includeContrast: {
        type: 'boolean',
        description:
          'Whether to include WCAG 2.1 AA/AAA relative luminance contrast validation results',
      },
      includeCss: {
        type: 'boolean',
        description: 'Whether to include generated Tailwind CSS v4 @theme variable definitions',
      },
    },
  },
  execute: (rawParams: Record<string, unknown>) => {
    const params = rawParams as ThemeTokensQueryParams;
    let targetSlug: ThemeSlug = 'swiss';

    if (!params.theme || params.theme === 'active') {
      const active = detectActiveTheme();
      targetSlug = (active.slug === 'default' ? 'swiss' : active.slug) as ThemeSlug;
    } else if (
      params.theme === 'swiss' ||
      params.theme === 'immersive' ||
      params.theme === 'freight' ||
      params.theme === 'luxury' ||
      params.theme === 'nordic'
    ) {
      targetSlug = params.theme;
    } else {
      targetSlug = 'swiss';
    }

    const palette = getCoreThemeTokens(targetSlug);
    const result: Record<string, unknown> = {
      theme: palette.name,
      displayName: palette.displayName,
      category: palette.category,
      order: palette.order,
      light: palette.light,
      dark: palette.dark,
      accent: palette.accent,
      status: palette.status,
      typography: palette.typography,
      radii: palette.radii,
    };

    if (params.includeContrast) {
      result.contrastReport = validateThemeContrast(palette);
    }

    if (params.includeCss) {
      result.tailwindCss = generateTailwindThemeCss(palette);
    }

    return result;
  },
};

export const getThemeContrastTool: WebMcpToolDefinition = {
  name: 'getThemeContrast',
  description:
    'Returns WCAG 2.1 AA and AAA accessibility contrast compliance reports across all 5 themes or for a specific theme, verifying ratios across headings, muted body text, and primary accent buttons.',
  inputSchema: {
    type: 'object',
    properties: {
      theme: {
        type: 'string',
        enum: ['swiss', 'immersive', 'freight', 'luxury', 'nordic', 'all', 'active'],
        description:
          'Theme slug to inspect ("swiss", "immersive", "freight", "luxury", "nordic", "active", or "all" to inspect all 5 themes)',
      },
    },
  },
  execute: (rawParams: Record<string, unknown>) => {
    const params = rawParams as ThemeContrastQueryParams;
    const allThemes: ThemeSlug[] = ['swiss', 'immersive', 'freight', 'luxury', 'nordic'];

    if (params.theme === 'all') {
      const reports = allThemes.map((slug) => {
        const palette = getCoreThemeTokens(slug);
        const report = validateThemeContrast(palette);
        return {
          theme: palette.name,
          displayName: palette.displayName,
          order: palette.order,
          allPassedAA: report.allPassedAA,
          allPassedAAA: report.allPassedAAA,
          checks: report.checks,
        };
      });
      return {
        success: true,
        count: reports.length,
        themes: reports,
      };
    }

    let targetSlug: ThemeSlug = 'swiss';
    if (!params.theme || params.theme === 'active') {
      const active = detectActiveTheme();
      targetSlug = (active.slug === 'default' ? 'swiss' : active.slug) as ThemeSlug;
    } else if (allThemes.includes(params.theme as ThemeSlug)) {
      targetSlug = params.theme as ThemeSlug;
    }

    const palette = getCoreThemeTokens(targetSlug);
    const report = validateThemeContrast(palette);
    return {
      success: true,
      theme: palette.name,
      displayName: palette.displayName,
      order: palette.order,
      allPassedAA: report.allPassedAA,
      allPassedAAA: report.allPassedAAA,
      checks: report.checks,
    };
  },
};

export interface ThemeInfoDto {
  name: string;
  slug: 'swiss' | 'immersive' | 'freight' | 'luxury' | 'nordic';
  order: number;
  category: string;
  accentColor: string;
  primaryFont: string;
  features: string[];
}

export const THEME_INFO_REGISTRY: ThemeInfoDto[] = [
  {
    name: 'Loumars Swiss Editorial',
    slug: 'swiss',
    order: 1000,
    category: 'High-Editorial Modernism & Precision Typography',
    accentColor: '#D90429',
    primaryFont: 'Neue Haas Grotesk',
    features: [
      'Minimalist drafting canvas',
      'Responsive navigation drawer',
      'Live Preview Studio',
      'Headless embed',
    ],
  },
  {
    name: 'Loumars Obsidian Telemetry HUD',
    slug: 'immersive',
    order: 1010,
    category: 'Mission-Critical Command, Telemetry Workbench & Developer HUD',
    accentColor: '#B04A2D',
    primaryFont: 'JetBrains Mono',
    features: [
      'Obsidian Telemetry HUD',
      'Real-time metrics stream',
      'CAD error logging HUD',
      'Radar sweep',
    ],
  },
  {
    name: 'Loumars Aerospace Titanium Freight',
    slug: 'freight',
    order: 1015,
    category: 'Aviation Cargo, Multimodal Logistics & Maritime Supply Chain',
    accentColor: '#1C64F2',
    primaryFont: 'Inter',
    features: [
      'Cargo aircraft payload estimator',
      'Multimodal deck inspector',
      'Cold-chain telemetry',
      'Distance & carbon burn engine',
    ],
  },
  {
    name: 'Loumars Champagne Luxury',
    slug: 'luxury',
    order: 1020,
    category: 'Minimalist Luxury Hospitality, Private Estates & High Editorial',
    accentColor: '#C9A86A',
    primaryFont: 'Playfair Display',
    features: [
      '4-sanctuary suite configurator',
      'Dynamic live currency conversion',
      'Nights duration slider',
      'Concierge modal dispatch',
    ],
  },
  {
    name: 'Loumars Nordic Architectural',
    slug: 'nordic',
    order: 1025,
    category: 'Nordic Architectural Studios, Raw Materiality & Scandinavian Minimalist',
    accentColor: '#8D99AE',
    primaryFont: 'Outfit',
    features: [
      'Nordic Architectural Studio dimension & carbon estimator',
      'Mass timber / granite / steel lifecycle calculation',
      'Daylight illumination ratio calculation',
      'Scandinavian materiality specifications',
    ],
  },
];

export const getThemeInfoTool: WebMcpToolDefinition = {
  name: 'getThemeInfo',
  description:
    'Returns discovery and architectural metadata for all 5 official Loumars themes or a specified theme, including routing order hierarchy, typography, accent color, and signature features.',
  inputSchema: {
    type: 'object',
    properties: {
      theme: {
        type: 'string',
        enum: ['swiss', 'immersive', 'freight', 'luxury', 'nordic', 'all', 'active'],
        description:
          'Theme slug to inspect ("swiss", "immersive", "freight", "luxury", "nordic", "active", or "all" to inspect all 5 themes). Defaults to "all".',
      },
    },
  },
  execute: (rawParams: Record<string, unknown>) => {
    const params = rawParams as { theme?: string };
    const routingOrderHierarchy =
      'swiss(1000) < immersive(1010) < freight(1015) < luxury(1020) < nordic(1025)';

    if (!params.theme || params.theme === 'all') {
      return {
        success: true,
        count: THEME_INFO_REGISTRY.length,
        themes: THEME_INFO_REGISTRY,
        routingOrderHierarchy,
      };
    }

    let targetSlug = params.theme.toLowerCase();
    if (targetSlug === 'active') {
      const active = detectActiveTheme();
      targetSlug = active.slug === 'default' ? 'swiss' : active.slug;
    }

    const matched = THEME_INFO_REGISTRY.find((t) => t.slug === targetSlug);
    if (matched) {
      return {
        success: true,
        theme: matched,
        routingOrderHierarchy,
      };
    }

    return {
      success: false,
      error: `Theme '${params.theme}' not found in official theme registry. Supported themes: swiss, immersive, freight, luxury, nordic.`,
      routingOrderHierarchy,
    };
  },
};

export const queryThemeWidgetsTool: WebMcpToolDefinition = {
  name: 'queryThemeWidgets',
  description:
    'Discovers and returns all widgets rendered on the active page with their widget types, content item IDs, element IDs, headings, and interactive capabilities.',
  inputSchema: {
    type: 'object',
    properties: {
      filterType: {
        type: 'string',
        description: 'Optional widget type filter (e.g. Hero, ContactForm, About, Services)',
      },
    },
  },
  execute: (params: Record<string, unknown>) => {
    if (typeof document === 'undefined') {
      return { count: 0, widgets: [] };
    }

    const filterType =
      typeof params.filterType === 'string' ? params.filterType.toLowerCase() : null;
    const elements = Array.from(
      document.querySelectorAll<HTMLElement>('[data-widget-type], [data-content-item-id]:not(body)')
    );
    const seen = new Set<HTMLElement>();
    const widgets: ThemeWidgetInfo[] = [];

    for (const el of elements) {
      if (seen.has(el)) continue;
      seen.add(el);

      const widgetType =
        el.getAttribute('data-widget-type') || el.getAttribute('data-content-type') || 'Unknown';
      if (filterType && !widgetType.toLowerCase().includes(filterType)) {
        continue;
      }

      const contentItemId = el.getAttribute('data-content-item-id') || undefined;
      const id = el.id || undefined;

      const headingEl = el.querySelector('h1, h2, h3, [data-widget-heading]');
      const heading = headingEl?.textContent ? headingEl.textContent.trim() : undefined;

      const capabilities: string[] = [];
      if (el.querySelector('form')) capabilities.push('form-submission');
      if (el.querySelector('[data-theme-toggle]')) capabilities.push('theme-toggle');
      if (el.querySelector('[data-accordion], .accordion, [data-accordion-trigger]'))
        capabilities.push('accordion');
      if (el.querySelector('[role="tab"], [data-tabs]')) capabilities.push('tabs');
      if (
        el.querySelector(
          'input[type="range"], [data-estimator], [data-cargo-estimator], loumars-cargo-estimator, .cargo-estimator'
        ) ||
        el.tagName.toLowerCase() === 'loumars-cargo-estimator'
      ) {
        capabilities.push('estimator-slider');
        capabilities.push('cargo-estimator');
      }
      if (
        el.querySelector(
          'button[data-suite], [data-suite-option], [data-suite-configurator], loumars-suite-configurator, .suite-configurator'
        ) ||
        el.tagName.toLowerCase() === 'loumars-suite-configurator'
      ) {
        capabilities.push('suite-selector');
        capabilities.push('suite-configurator');
      }
      if (
        el.querySelector(
          '[data-nordic-calculator], [data-calc-width], [data-ne-material], loumars-nordic-studio'
        )
      )
        capabilities.push('architectural-calculator');
      if (
        el.querySelector('[data-service-workbench], [data-hud-container], loumars-telemetry-hud') ||
        el.tagName.toLowerCase() === 'loumars-telemetry-hud'
      )
        capabilities.push('telemetry-hud');
      if (
        el.querySelector(
          '[data-drafting-studio], [data-drafting-canvas], [data-preview-canvas], loumars-drafting-studio'
        ) ||
        el.tagName.toLowerCase() === 'loumars-drafting-studio'
      )
        capabilities.push('drafting-studio');
      if (
        el.querySelector(
          '[data-freight-tracker], [data-tracker-form], [data-tracker-input], #awb-tracking-input'
        ) ||
        el.hasAttribute('data-freight-tracker') ||
        widgetType.toLowerCase().includes('tracker')
      )
        capabilities.push('consignment-tracker');
      if (
        el.querySelector(
          '[data-burn-weight], [data-carbon-slider], [data-carbon-calculator], #carbon-origin, #carbon-destination'
        ) ||
        el.hasAttribute('data-burn-weight') ||
        el.hasAttribute('data-carbon-calculator') ||
        widgetType.toLowerCase().includes('carboncalculator')
      ) {
        capabilities.push('carbon-calculator');
        capabilities.push('emissions-calculator');
      }
      if (
        el.querySelector(
          '[data-flight-corridors], [data-trade-corridors], [data-routes-network], #routes'
        ) ||
        el.hasAttribute('data-flight-corridors') ||
        el.hasAttribute('data-trade-corridors') ||
        el.id === 'routes' ||
        widgetType.toLowerCase().includes('routes') ||
        widgetType.toLowerCase().includes('flightcorridors')
      ) {
        capabilities.push('trade-corridors');
        capabilities.push('flight-corridors');
      }
      if (
        el.querySelector(
          '[data-fleet-specs], [data-aircraft-grid], .fleet-matrix, [data-widget-type*="Fleet"]'
        ) ||
        el.hasAttribute('data-fleet-specs') ||
        el.id === 'fleet' ||
        widgetType.toLowerCase().includes('fleet')
      ) {
        capabilities.push('fleet-inspector');
        capabilities.push('aircraft-fleet');
        capabilities.push('fleet-specs');
      }
      if (
        el.querySelector(
          '[data-hub-code], [data-hubs-grid], .map-container, #map, [data-widget-type*="Map"]'
        ) ||
        el.hasAttribute('data-hub-code') ||
        el.id === 'map' ||
        widgetType.toLowerCase().includes('map')
      ) {
        capabilities.push('logistics-hubs');
        capabilities.push('gateway-map');
        capabilities.push('ports-terminal');
      }
      if (
        el.querySelector(
          '[data-cold-chain], [data-qa-zone], .coldchain-container, [data-widget-type*="ColdChain"], [data-widget-type*="QualityAssurance"]'
        ) ||
        el.hasAttribute('data-cold-chain') ||
        el.id === 'coldchain' ||
        widgetType.toLowerCase().includes('coldchain') ||
        widgetType.toLowerCase().includes('qualityassurance')
      ) {
        capabilities.push('cold-chain');
        capabilities.push('quality-assurance');
        capabilities.push('temperature-zones');
      }
      if (
        el.querySelector(
          '[data-compliance], [data-certification], .compliance-container, [data-widget-type*="Compliance"], [data-widget-type*="FreightCompliance"]'
        ) ||
        el.hasAttribute('data-compliance') ||
        el.id === 'compliance' ||
        widgetType.toLowerCase().includes('compliance')
      ) {
        capabilities.push('compliance');
        capabilities.push('regulatory-matrix');
        capabilities.push('certifications');
      }
      if (
        el.querySelector(
          '[data-pricing], [data-rate-cards], .pricing-grid, [data-widget-type*="Pricing"], [data-widget-type*="PricingPlan"]'
        ) ||
        el.hasAttribute('data-pricing') ||
        el.id === 'pricing' ||
        widgetType.toLowerCase().includes('pricing')
      ) {
        capabilities.push('pricing');
        capabilities.push('rate-cards');
        capabilities.push('plans');
      }
      if (
        el.querySelector(
          '[data-careers], [data-jobs], [data-requisitions], .careers-container, [data-widget-type*="Careers"], [data-widget-type*="JobPostingItem"]'
        ) ||
        el.hasAttribute('data-careers') ||
        el.id === 'careers' ||
        widgetType.toLowerCase().includes('careers') ||
        widgetType.toLowerCase().includes('jobposting')
      ) {
        capabilities.push('careers');
        capabilities.push('jobs');
        capabilities.push('requisitions');
        capabilities.push('job-postings');
      }
      if (
        el.querySelector(
          '[data-case-studies], [data-case-study], [data-post-mortems], .case-study-container, [data-widget-type*="CaseStudy"]'
        ) ||
        el.hasAttribute('data-case-studies') ||
        el.hasAttribute('data-case-study') ||
        el.id === 'case-studies' ||
        widgetType.toLowerCase().includes('casestudy') ||
        widgetType.toLowerCase().includes('postmortem')
      ) {
        capabilities.push('case-studies');
        capabilities.push('case-study');
        capabilities.push('post-mortems');
        capabilities.push('stories');
      }
      if (
        el.querySelector(
          '[data-testimonials], [data-testimonial], [data-reviews], [data-endorsements], .testimonial-container, [data-widget-type*="Testimonial"]'
        ) ||
        el.hasAttribute('data-testimonials') ||
        el.hasAttribute('data-testimonial') ||
        el.hasAttribute('data-reviews') ||
        el.id === 'testimonials' ||
        widgetType.toLowerCase().includes('testimonial') ||
        widgetType.toLowerCase().includes('review')
      ) {
        capabilities.push('testimonials');
        capabilities.push('testimonial');
        capabilities.push('reviews');
        capabilities.push('endorsements');
      }
      if (
        el.querySelector(
          '[data-team], [data-team-member], [data-leadership], .team-container, #team, [data-widget-type*="Team"]'
        ) ||
        el.hasAttribute('data-team') ||
        el.hasAttribute('data-team-member') ||
        el.id === 'team' ||
        widgetType.toLowerCase().includes('team') ||
        widgetType.toLowerCase().includes('teammember')
      ) {
        capabilities.push('team');
        capabilities.push('team-roster');
        capabilities.push('leadership');
        capabilities.push('members');
      }
      if (
        el.querySelector(
          '[data-accordion], [data-faq], [data-disclosure], .accordion-container, details, [data-widget-type*="Accordion"]'
        ) ||
        el.hasAttribute('data-accordion') ||
        el.hasAttribute('data-faq') ||
        el.hasAttribute('data-disclosure') ||
        el.tagName.toLowerCase() === 'details' ||
        widgetType.toLowerCase().includes('accordion') ||
        widgetType.toLowerCase().includes('faq')
      ) {
        capabilities.push('accordion');
        capabilities.push('faq');
        capabilities.push('disclosures');
        capabilities.push('knowledge-base');
      }
      if (
        el.querySelector(
          '[data-newsletter], [data-newsletter-form], #acms-newsletter-form, #immersive-newsletter-form, form[toolname="subscribeNewsletter"], [data-widget-type*="Newsletter"]'
        ) ||
        el.hasAttribute('data-newsletter') ||
        el.id === 'acms-newsletter-form' ||
        el.id === 'immersive-newsletter-form' ||
        widgetType.toLowerCase().includes('newsletter')
      ) {
        capabilities.push('newsletter');
        capabilities.push('newsletter-signup');
        capabilities.push('subscription');
      }
      if (
        el.querySelector(
          '[data-capabilities], [data-capability-matrix], [data-solutions], .capabilities-grid, #capabilities, [data-widget-type*="Capabilities"], [data-widget-type*="FreightServices"]'
        ) ||
        el.hasAttribute('data-capabilities') ||
        el.id === 'capabilities' ||
        widgetType.toLowerCase().includes('capabilities') ||
        widgetType.toLowerCase().includes('freightservices')
      ) {
        capabilities.push('capabilities');
        capabilities.push('capability-matrix');
        capabilities.push('solutions');
      }

      widgets.push({
        widgetType,
        contentItemId,
        id,
        heading,
        isInteractive: capabilities.length > 0,
        capabilities,
      });
    }

    return {
      count: widgets.length,
      widgets,
    };
  },
};

import {
  CANONICAL_58_WIDGETS,
  type CanonicalWidgetContract,
} from './widget-registry';

export {
  CANONICAL_58_WIDGETS,
  type CanonicalWidgetContract,
};

export const getCanonicalWidgetRegistryTool: WebMcpToolDefinition = {
  name: 'getCanonicalWidgetRegistry',
  description:
    'Returns the canonical 58-widget contract registry across the Loumars Quad-Theme Ecosystem, supporting category and widget type filtering.',
  inputSchema: {
    type: 'object',
    properties: {
      category: {
        type: 'string',
        description:
          'Optional category filter ("Navigation & Layout", "Hero & Callouts", "Content & Storytelling", "Modular Cards & Grids", "Social & Trust", "Interactive Dynamic Elements", "Industry & Specialty Engines")',
      },
      type: {
        type: 'string',
        description: 'Optional specific widget type (e.g. "About", "Services", "Hero", "Pricing")',
      },
    },
  },
  execute: (params: Record<string, unknown>) => {
    const category = typeof params.category === 'string' ? params.category.trim() : undefined;
    const type = typeof params.type === 'string' ? params.type.trim() : undefined;

    if (type) {
      const match = CANONICAL_58_WIDGETS.find((w) => w.widgetType.toLowerCase() === type.toLowerCase());
      if (match) {
        return { success: true, widget: match };
      }
      return { success: false, error: `Widget type "${type}" not found in canonical 58-widget registry.` };
    }

    if (category) {
      const filtered = CANONICAL_58_WIDGETS.filter((w) =>
        w.category.toLowerCase().includes(category.toLowerCase())
      );
      return {
        success: true,
        count: filtered.length,
        totalCanonicalCount: CANONICAL_58_WIDGETS.length,
        categoryFilter: category,
        widgets: filtered,
      };
    }

    return {
      success: true,
      count: CANONICAL_58_WIDGETS.length,
      parityInvariant:
        '100% 58/58 template symmetry enforced across Swiss, Immersive, Freight, Luxury, and Nordic themes.',
      widgets: CANONICAL_58_WIDGETS,
    };
  },
};

export const getThemeHealthTool: WebMcpToolDefinition = {
  name: 'getThemeHealth',
  description:
    'Returns the comprehensive multi-point health status of the Loumars Theme Ecosystem, verifying 5-theme count, routing order hierarchy, 58-widget parity invariants, standalone assets compliance, and antiforgery security policies.',
  inputSchema: {
    type: 'object',
    properties: {},
  },
  execute: () => {
    return {
      success: true,
      status: 'Healthy',
      themeCount: 5,
      themes: ['swiss', 'immersive', 'freight', 'luxury', 'nordic'],
      routingOrderHierarchy: 'swiss(1000) < immersive(1010) < freight(1015) < luxury(1020) < nordic(1025)',
      canonicalWidgetCount: CANONICAL_58_WIDGETS.length,
      totalVerifiedTemplates: CANONICAL_58_WIDGETS.length * 5,
      parityInvariant: `100% ${CANONICAL_58_WIDGETS.length}/${CANONICAL_58_WIDGETS.length} template symmetry enforced across Swiss, Immersive, Freight, Luxury, and Nordic themes (${CANONICAL_58_WIDGETS.length * 5} verified templates).`,
      assetsPolicy: 'Compliant: Standalone CSS/JS assets, Zero inline scripts/styles',
      antiforgeryPolicy: 'Compliant: [AutoValidateAntiforgeryToken] and validation summary triplets present',
    };
  },
};

export const getWidgetCategoriesTool: WebMcpToolDefinition = {
  name: 'getWidgetCategories',
  description:
    'Returns the canonical 58 widgets grouped across the 7 structural categories with member widget lists, category counts, and FlowPart container statistics.',
  inputSchema: {
    type: 'object',
    properties: {
      category: {
        type: 'string',
        description:
          'Optional category name filter ("Navigation & Layout", "Hero & Callouts", "Content & Storytelling", "Modular Cards & Grids", "Social & Trust", "Interactive Dynamic Elements", "Industry & Specialty Engines")',
      },
    },
  },
  execute: (params: Record<string, unknown>) => {
    const categoryFilter = typeof params.category === 'string' ? params.category.trim() : undefined;
    const categoryMap = new Map<
      string,
      { name: string; count: number; flowPartContainerCount: number; widgets: string[] }
    >();

    for (const widget of CANONICAL_58_WIDGETS) {
      let cat = categoryMap.get(widget.category);
      if (!cat) {
        cat = { name: widget.category, count: 0, flowPartContainerCount: 0, widgets: [] };
        categoryMap.set(widget.category, cat);
      }
      cat.count++;
      if (widget.supportsFlowPart) cat.flowPartContainerCount++;
      cat.widgets.push(widget.widgetType);
    }

    let categories = Array.from(categoryMap.values()).sort((a, b) => a.name.localeCompare(b.name));
    for (const c of categories) {
      c.widgets.sort();
    }

    if (categoryFilter) {
      categories = categories.filter((c) =>
        c.name.toLowerCase().includes(categoryFilter.toLowerCase())
      );
    }

    return {
      success: true,
      totalCategories: categories.length,
      totalWidgets: CANONICAL_58_WIDGETS.length,
      categories,
    };
  },
};

export const interactWithWidgetTool: WebMcpToolDefinition = {
  name: 'interactWithWidget',
  description:
    'Dispatches an action on an interactive widget (e.g. toggles an accordion item, switches tabs, or triggers cart drawers).',
  inputSchema: {
    type: 'object',
    required: ['action'],
    properties: {
      action: {
        type: 'string',
        description:
          'Action name: "toggleAccordion", "switchTab", "toggleCartDrawer", "calculateNordicDimensions", "queryTokens", "queryContrast", "queryThemeInfo", "switchThemePreview", "queryTelemetry", "toggleHudFeature", "queryDraftingMetrics", "configureDraftingCanvas", "queryWidgetContract", "querySuiteQuote", "configureSuite", "toggleAmenity", "setAmenities", "queryCargoEstimate", "calculateCargoEstimate", "queryEcosystemHealth", "queryWidgetCategories", "setThemeMode", "queryThemeState", "querySiteOverview", "submitContactInquiry", "searchSite", "trackConsignment", "calculateEmissions", "queryTradeCorridors", "queryFleet", "queryAircraft", "queryHubs", "queryPorts", "queryGateways", "queryColdChain", "queryTemperatureZones", "queryQa", "queryCompliance", "queryCertifications", "queryRegulatory", "queryPricing", "queryPlans", "queryRateCards", "queryCareers", "queryJobs", "queryRequisitions", "queryCaseStudies", "queryStories", "queryPostMortems", "queryTestimonials", "queryReviews", "queryEndorsements", "queryTeam", "queryLeadership", "queryMembers", "queryFaq", "queryAccordion", "queryDisclosures", "subscribeNewsletter", "queryNewsletterTopics"',
      },
      targetId: {
        type: 'string',
        description: 'Target element ID or selector for the widget item',
      },
      index: {
        type: 'string',
        description: 'Numeric 0-based index of item to activate (for accordions or tabs)',
      },
      mode: {
        type: 'string',
        description: 'Target mode ("light", "dark", "system") for switchThemePreview action',
      },
      amenity: {
        type: 'string',
        description: 'Target amenity ID for toggleAmenity action',
      },
      feature: {
        type: 'string',
        description: 'HUD feature name ("radar", "scanlines", "flicker", "sound") for toggleHudFeature action',
      },
      enabled: {
        type: 'boolean',
        description: 'Target enabled state for toggleHudFeature action',
      },
      viewport: {
        type: 'string',
        description: 'Target viewport ("desktop", "tablet", "mobile", "ultrawide") for configureDraftingCanvas action',
      },
      scale: {
        type: 'string',
        description: 'Target scale ratio ("1:100", "1:50", "1:20", "1:10") for configureDraftingCanvas action',
      },
      atmosphere: {
        type: 'string',
        description: 'Target atmosphere ("light", "obsidian") for configureDraftingCanvas action',
      },
      curvature: {
        type: 'string',
        description: 'Target curvature ("sharp", "technical", "tactile", "pill") for configureDraftingCanvas action',
      },
      grid: {
        type: 'boolean',
        description: 'Whether CAD grid overlay is enabled for configureDraftingCanvas action',
      },
      suite: {
        type: 'string',
        description: 'Target suite ID ("ocean", "villa", "penthouse", "estate") for configureSuite or querySuiteQuote action',
      },
      nights: {
        type: 'string',
        description: 'Stay duration in nights for configureSuite or querySuiteQuote action',
      },
      currency: {
        type: 'string',
        description: 'Currency code ("USD", "EUR", "GBP", "CHF", "JPY") for configureSuite or querySuiteQuote action',
      },
      amenities: {
        type: 'string',
        description: 'Selected amenities comma-separated or ID array for configureSuite or querySuiteQuote action',
      },
      model: {
        type: 'string',
        description: 'Cargo vessel/freighter model code ("ULCV24", "NEOPAN15", "FEEDER3", "B777F", "B747-8F", "A330-200F") for calculateCargoEstimate or queryCargoEstimate action',
      },
      weight: {
        type: 'string',
        description: 'Cargo weight in kg for calculateCargoEstimate or queryCargoEstimate action',
      },
      volume: {
        type: 'string',
        description: 'Cargo volume in m3 for calculateCargoEstimate or queryCargoEstimate action',
      },
      theme: {
        type: 'string',
        description: 'Target theme slug for queryContrast action ("swiss", "immersive", "freight", "luxury", "nordic", "all")',
      },
      query: {
        type: 'string',
        description: 'Search query for searchSite action',
      },
      trackingNumber: {
        type: 'string',
        description: 'Consignment or AWB tracking number for trackConsignment action',
      },
      name: {
        type: 'string',
        description: 'Sender name for submitContactInquiry action',
      },
      email: {
        type: 'string',
        description: 'Sender email for submitContactInquiry action',
      },
      phone: {
        type: 'string',
        description: 'Sender phone for submitContactInquiry action',
      },
      company: {
        type: 'string',
        description: 'Sender company for submitContactInquiry action',
      },
      serviceType: {
        type: 'string',
        description: 'Requested service type for submitContactInquiry action',
      },
      origin: {
        type: 'string',
        description: 'Origin location for submitContactInquiry action',
      },
      destination: {
        type: 'string',
        description: 'Destination location for submitContactInquiry action',
      },
      message: {
        type: 'string',
        description: 'Message content for submitContactInquiry action',
      },
      cargoWeightTons: {
        type: 'string',
        description: 'Cargo weight in metric tons for calculateEmissions action',
      },
      sustainableFuelPercent: {
        type: 'string',
        description: 'Sustainable fuel blend percentage (0-100) for calculateEmissions action',
      },
      modelCode: {
        type: 'string',
        description:
          'Fleet or aircraft model code (e.g. B777F, B747-8F, A330P2F, B767-300BCF, A321P2F, ULCV24) for queryFleet action',
      },
      manufacturer: {
        type: 'string',
        description: 'Manufacturer filter for queryFleet action',
      },
      code: {
        type: 'string',
        description:
          'Logistics hub or gateway code (e.g. CEB, SIN, SHA, ROT, LAX, FRA, DXB, ANC) for queryHubs action',
      },
      zoneCode: {
        type: 'string',
        description:
          'Thermal precision zone code (e.g. "CRY", "FRZ", "REF", "CRT", "WARM") for queryColdChain action',
      },
      zone: {
        type: 'string',
        description:
          'Thermal zone name or code alias for queryColdChain action',
      },
      packaging: {
        type: 'string',
        description:
          'Packaging technology filter for queryColdChain action',
      },
      certification: {
        type: 'string',
        description:
          'Regulatory certification filter for queryColdChain or queryCompliance action',
      },
      authority: {
        type: 'string',
        description:
          'Regulatory authority filter (e.g. "WCO", "CBP", "IATA", "TAPA", "ISO", "PEZA") for queryCompliance action',
      },
      tier: {
        type: 'string',
        description:
          'Pricing tier or plan filter (e.g. "ECONOMY_LCL", "STANDARD_CARGO", "EXPRESS_AIR", "COLD_CHAIN_PHARMA", "AEROSPACE_CHARTER", "SANCTUARY_RETREAT") for queryPricing action',
      },
      plan: {
        type: 'string',
        description: 'Pricing plan name or code alias for queryPricing action',
      },
      transportMode: {
        type: 'string',
        description:
          'Transport mode filter (e.g. "Ocean/Ground", "Multimodal", "Air Express", "Active Cold-Chain", "Air Charter", "Concierge") for queryPricing action',
      },
    },
  },
  execute: (params: Record<string, unknown>) => {
    if (typeof document === 'undefined') {
      return { success: false, error: 'Document not available' };
    }

    const action = String(params.action || '');
    const targetId = typeof params.targetId === 'string' ? params.targetId : null;
    const index =
      typeof params.index === 'number'
        ? params.index
        : typeof params.index === 'string'
          ? parseInt(params.index, 10)
          : 0;

    if (action === 'queryTokens') {
      const active = detectActiveTheme();
      const slug = (active.slug === 'default' ? 'swiss' : active.slug) as ThemeSlug;
      const palette = getCoreThemeTokens(slug);
      return {
        success: true,
        action,
        theme: palette.name,
        displayName: palette.displayName,
        palette,
        status: `Retrieved design tokens for ${palette.displayName}.`,
      };
    }

    if (action === 'switchThemePreview') {
      const mode = typeof params.mode === 'string' ? params.mode : undefined;
      const win =
        typeof window !== 'undefined'
          ? (window as unknown as {
              afSetThemeMode?: (m: string) => void;
              toggleStudioThemeMode?: (m?: 'light' | 'dark' | 'system') => void;
            })
          : null;
      if (win && typeof win.afSetThemeMode === 'function' && mode) {
        win.afSetThemeMode(mode);
      } else if (win && typeof win.toggleStudioThemeMode === 'function') {
        win.toggleStudioThemeMode(mode as 'light' | 'dark' | 'system' | undefined);
      }
      return {
        success: true,
        action,
        mode: mode || 'toggle',
        status: 'Theme preview mode switch dispatched.',
      };
    }

    if (action === 'toggleAccordion') {
      let trigger: HTMLElement | null = null;
      if (targetId) {
        trigger =
          document.getElementById(targetId) || document.querySelector<HTMLElement>(targetId);
      }
      if (!trigger) {
        const triggers = document.querySelectorAll<HTMLElement>(
          '[data-accordion-trigger], [data-accordion-header], summary'
        );
        if (triggers.length > index) {
          trigger = triggers[index];
        }
      }
      if (trigger) {
        trigger.click();
        return { success: true, action, status: 'Accordion trigger clicked.' };
      }
      return { success: false, action, error: 'No matching accordion trigger found.' };
    }

    if (action === 'switchTab') {
      let tabBtn: HTMLElement | null = null;
      if (targetId) {
        tabBtn = document.getElementById(targetId) || document.querySelector<HTMLElement>(targetId);
      }
      if (!tabBtn) {
        const tabs = document.querySelectorAll<HTMLElement>('[role="tab"], [data-tab-trigger]');
        if (tabs.length > index) {
          tabBtn = tabs[index];
        }
      }
      if (tabBtn) {
        tabBtn.click();
        return { success: true, action, status: 'Tab trigger clicked.' };
      }
      return { success: false, action, error: 'No matching tab trigger found.' };
    }

    if (action === 'toggleCartDrawer') {
      if (typeof window !== 'undefined' && typeof window.afToggleCartDrawer === 'function') {
        window.afToggleCartDrawer();
        return {
          success: true,
          action,
          status: 'Cart drawer toggled via window.afToggleCartDrawer.',
        };
      }
      const cartBtn = document.querySelector<HTMLElement>(
        '[data-cart-toggle], [data-shopify-cart-trigger]'
      );
      if (cartBtn) {
        cartBtn.click();
        return { success: true, action, status: 'Cart drawer toggled via trigger button.' };
      }
      return { success: false, action, error: 'Cart drawer trigger not found.' };
    }

    if (action === 'calculateNordicDimensions') {
      const container = targetId
        ? document.getElementById(targetId) || document.querySelector<HTMLElement>(targetId)
        : document.querySelector<HTMLElement>('[data-nordic-calculator], loumars-nordic-studio');
      if (container) {
        if (container.tagName.toLowerCase() === 'loumars-nordic-studio') {
          const ce = container as unknown as {
            getEstimate?: () => unknown;
          };
          if (typeof ce.getEstimate === 'function') {
            return {
              success: true,
              action,
              estimate: ce.getEstimate(),
              status: 'Nordic studio custom element estimate retrieved.',
            };
          }
        }
        return {
          success: true,
          action,
          status: 'Nordic architectural calculator container located and inspected.',
        };
      }
      return { success: false, action, error: 'Nordic architectural calculator not found.' };
    }

    if (action === 'queryTelemetry') {
      const container = targetId
        ? document.getElementById(targetId) || document.querySelector<HTMLElement>(targetId)
        : document.querySelector<HTMLElement>('loumars-telemetry-hud, [data-service-workbench]');
      if (container) {
        if (container.tagName.toLowerCase() === 'loumars-telemetry-hud') {
          const ce = container as unknown as {
            getMetrics?: () => unknown;
          };
          if (typeof ce.getMetrics === 'function') {
            return {
              success: true,
              action,
              metrics: ce.getMetrics(),
              status: 'Telemetry HUD custom element metrics retrieved.',
            };
          }
        }
        const mttrEl = container.querySelector('[data-metric-mttr], [data-hud-mttr]');
        const coverageEl = container.querySelector('[data-metric-coverage], [data-hud-coverage]');
        const slaEl = container.querySelector('[data-metric-sla], [data-hud-sla]');
        const latencyEl = container.querySelector('[data-stream-latency], [data-hud-latency]');
        return {
          success: true,
          action,
          metrics: {
            mttr: mttrEl?.textContent || null,
            coverage: coverageEl?.textContent || null,
            sla: slaEl?.textContent || null,
            latency: latencyEl?.textContent || null,
          },
          status: 'Workbench telemetry metrics retrieved.',
        };
      }
      return { success: false, action, error: 'Telemetry HUD or workbench not found.' };
    }

    if (action === 'toggleHudFeature') {
      const feature = typeof params.feature === 'string' ? params.feature : 'scanlines';
      const enabled = params.enabled !== undefined ? Boolean(params.enabled) : true;
      const hudEl = document.querySelector<HTMLElement>('loumars-telemetry-hud');
      if (hudEl) {
        const ce = hudEl as unknown as {
          setToggle?: (f: string, e: boolean) => void;
        };
        if (typeof ce.setToggle === 'function') {
          ce.setToggle(feature, enabled);
          return {
            success: true,
            action,
            feature,
            enabled,
            status: `HUD feature "${feature}" toggled on custom element.`,
          };
        }
      }
      const win =
        typeof window !== 'undefined'
          ? (window as unknown as {
              afSetHudToggle?: (f: string, e: boolean) => void;
            })
          : null;
      if (win && typeof win.afSetHudToggle === 'function') {
        win.afSetHudToggle(feature, enabled);
        return {
          success: true,
          action,
          feature,
          enabled,
          status: `HUD feature "${feature}" toggled via window.afSetHudToggle.`,
        };
      }
      const toggleInput = document.querySelector<HTMLInputElement>(
        `[data-hud-toggle="${feature}"], #param-${feature === 'scanlines' ? 'crt-scanlines' : feature === 'radar' ? 'radar-sweep' : feature === 'flicker' ? 'telemetry-flicker' : 'hud-audio'}`
      );
      if (toggleInput) {
        toggleInput.checked = enabled;
        toggleInput.dispatchEvent(new Event('change', { bubbles: true }));
        return {
          success: true,
          action,
          feature,
          enabled,
          status: `HUD feature "${feature}" toggled via checkbox input.`,
        };
      }
      return { success: false, action, error: `HUD toggle for feature "${feature}" not found.` };
    }

    if (action === 'queryDraftingMetrics') {
      const container = targetId
        ? document.getElementById(targetId) || document.querySelector<HTMLElement>(targetId)
        : document.querySelector<HTMLElement>('loumars-drafting-studio, [data-drafting-canvas]');
      if (container) {
        if (container.tagName.toLowerCase() === 'loumars-drafting-studio') {
          const ce = container as unknown as {
            getMetrics?: () => unknown;
          };
          if (typeof ce.getMetrics === 'function') {
            return {
              success: true,
              action,
              metrics: ce.getMetrics(),
              status: 'Swiss Editorial drafting studio custom element metrics retrieved.',
            };
          }
        }
        return {
          success: true,
          action,
          status: 'Drafting canvas container located and inspected.',
        };
      }
      return { success: false, action, error: 'Drafting studio or canvas not found.' };
    }

    if (action === 'configureDraftingCanvas') {
      const studioEl = document.querySelector<HTMLElement>('loumars-drafting-studio');
      if (studioEl) {
        const ce = studioEl as unknown as {
          setViewport?: (vp: string) => void;
          setScale?: (s: string) => void;
          setAtmosphere?: (a: string) => void;
          setCurvature?: (c: string) => void;
          setGrid?: (g: boolean) => void;
          getMetrics?: () => unknown;
        };
        if (typeof params.viewport === 'string' && typeof ce.setViewport === 'function') {
          ce.setViewport(params.viewport);
        }
        if (typeof params.scale === 'string' && typeof ce.setScale === 'function') {
          ce.setScale(params.scale);
        }
        if (typeof params.atmosphere === 'string' && typeof ce.setAtmosphere === 'function') {
          ce.setAtmosphere(params.atmosphere);
        }
        if (typeof params.curvature === 'string' && typeof ce.setCurvature === 'function') {
          ce.setCurvature(params.curvature);
        }
        if (typeof params.grid === 'boolean' && typeof ce.setGrid === 'function') {
          ce.setGrid(params.grid);
        }
        return {
          success: true,
          action,
          metrics: typeof ce.getMetrics === 'function' ? ce.getMetrics() : undefined,
          status: 'Drafting studio canvas reconfigured.',
        };
      }
      return { success: false, action, error: 'loumars-drafting-studio custom element not found.' };
    }

    if (action === 'queryWidgetContract') {
      const type =
        typeof params.widgetType === 'string'
          ? params.widgetType
          : typeof params.targetId === 'string'
            ? params.targetId
            : undefined;
      if (!type) {
        return {
          success: false,
          action,
          error: 'widgetType or targetId parameter required for queryWidgetContract.',
        };
      }
      const match = CANONICAL_58_WIDGETS.find(
        (w) => w.widgetType.toLowerCase() === type.toLowerCase()
      );
      if (match) {
        return { success: true, action, widget: match };
      }
      return { success: false, action, error: `Widget type "${type}" not found in canonical registry.` };
    }

    if (action === 'querySuiteQuote') {
      const container = targetId
        ? document.getElementById(targetId) || document.querySelector<HTMLElement>(targetId)
        : document.querySelector<HTMLElement>('loumars-suite-configurator, [data-suite-configurator]');
      if (container && container.tagName.toLowerCase() === 'loumars-suite-configurator') {
        const ce = container as unknown as { getQuote?: () => unknown };
        if (typeof ce.getQuote === 'function') {
          return {
            success: true,
            action,
            quote: ce.getQuote(),
            status: 'Luxury suite configurator quote retrieved.',
          };
        }
      }

      const suiteId = typeof params.suite === 'string' ? params.suite.toLowerCase() : 'ocean';
      const suite = CANONICAL_SUITES[suiteId] || CANONICAL_SUITES.ocean;
      const nights =
        typeof params.nights === 'number'
          ? params.nights
          : typeof params.nights === 'string'
            ? parseInt(params.nights, 10)
            : 3;
      const currency = (
        typeof params.currency === 'string' ? params.currency.toUpperCase() : 'USD'
      ) as LuxuryCurrencyCode;
      const rawAmenities = params.amenities;
      const amenityIds = Array.isArray(rawAmenities)
        ? rawAmenities.map(String)
        : typeof rawAmenities === 'string'
          ? rawAmenities.split(',').map((s) => s.trim().toLowerCase())
          : [];
      const amenitiesList = amenityIds
        .map((id) => CANONICAL_AMENITIES[id])
        .filter(Boolean);

      const quote = calculateSuiteQuote(suite.rate, nights, amenitiesList, currency);
      return {
        success: true,
        action,
        quote,
        status: 'Calculated luxury sanctuary suite quote.',
      };
    }

    if (action === 'configureSuite') {
      const suiteEl = document.querySelector<HTMLElement>('loumars-suite-configurator');
      if (suiteEl) {
        const ce = suiteEl as unknown as {
          setSuite?: (s: string) => void;
          setNights?: (n: number) => void;
          setCurrency?: (c: LuxuryCurrencyCode) => void;
          setAmenities?: (a: string[] | string) => void;
          getQuote?: () => unknown;
        };
        if (typeof params.suite === 'string' && typeof ce.setSuite === 'function') {
          ce.setSuite(params.suite);
        }
        if (params.nights !== undefined && typeof ce.setNights === 'function') {
          const n = typeof params.nights === 'number' ? params.nights : parseInt(String(params.nights), 10);
          ce.setNights(n);
        }
        if (typeof params.currency === 'string' && typeof ce.setCurrency === 'function') {
          ce.setCurrency(params.currency.toUpperCase() as LuxuryCurrencyCode);
        }
        if (params.amenities !== undefined && typeof ce.setAmenities === 'function') {
          ce.setAmenities(params.amenities as string[] | string);
        }
        return {
          success: true,
          action,
          quote: typeof ce.getQuote === 'function' ? ce.getQuote() : undefined,
          status: 'Luxury suite configurator reconfigured.',
        };
      }
      return { success: false, action, error: 'loumars-suite-configurator custom element not found.' };
    }

    if (action === 'toggleAmenity') {
      const suiteEl = document.querySelector<HTMLElement>('loumars-suite-configurator');
      if (suiteEl) {
        const ce = suiteEl as unknown as {
          toggleAmenity?: (a: string, ch?: boolean) => void;
          getQuote?: () => unknown;
        };
        const amenity = typeof params.amenity === 'string' ? params.amenity : undefined;
        if (!amenity) {
          return { success: false, action, error: 'amenity parameter required for toggleAmenity.' };
        }
        if (typeof ce.toggleAmenity === 'function') {
          ce.toggleAmenity(
            amenity,
            typeof params.enabled === 'boolean' ? params.enabled : undefined
          );
        }
        return {
          success: true,
          action,
          amenity,
          quote: typeof ce.getQuote === 'function' ? ce.getQuote() : undefined,
          status: `Luxury amenity "${amenity}" toggled.`,
        };
      }
      return { success: false, action, error: 'loumars-suite-configurator custom element not found.' };
    }

    if (action === 'setAmenities') {
      const suiteEl = document.querySelector<HTMLElement>('loumars-suite-configurator');
      if (suiteEl) {
        const ce = suiteEl as unknown as {
          setAmenities?: (a: string[] | string) => void;
          getQuote?: () => unknown;
        };
        if (params.amenities !== undefined && typeof ce.setAmenities === 'function') {
          ce.setAmenities(params.amenities as string[] | string);
        }
        return {
          success: true,
          action,
          quote: typeof ce.getQuote === 'function' ? ce.getQuote() : undefined,
          status: 'Luxury suite amenities updated.',
        };
      }
      return { success: false, action, error: 'loumars-suite-configurator custom element not found.' };
    }

    if (action === 'queryCargoEstimate') {
      const container = targetId
        ? document.getElementById(targetId) || document.querySelector<HTMLElement>(targetId)
        : document.querySelector<HTMLElement>('loumars-cargo-estimator, [data-cargo-estimator]');
      if (container && container.tagName.toLowerCase() === 'loumars-cargo-estimator') {
        const ce = container as unknown as { getEstimate?: () => unknown };
        if (typeof ce.getEstimate === 'function') {
          return {
            success: true,
            action,
            estimate: ce.getEstimate(),
            status: 'Freight cargo estimator payload estimate retrieved.',
          };
        }
      }

      const model = typeof params.model === 'string' ? params.model : 'ULCV24';
      const weight =
        typeof params.weight === 'number'
          ? params.weight
          : typeof params.weight === 'string'
            ? parseFloat(params.weight)
            : 45000;
      const volume =
        typeof params.volume === 'number'
          ? params.volume
          : typeof params.volume === 'string'
            ? parseFloat(params.volume)
            : 320;
      const estimate = calculateCargoEstimate(model, weight, volume);
      return {
        success: true,
        action,
        estimate,
        status: 'Calculated freight cargo payload estimate.',
      };
    }

    if (action === 'calculateCargoEstimate') {
      const cargoEl = document.querySelector<HTMLElement>('loumars-cargo-estimator');
      if (cargoEl) {
        const ce = cargoEl as unknown as {
          setModel?: (m: string) => void;
          setWeight?: (w: number) => void;
          setVolume?: (v: number) => void;
          getEstimate?: () => unknown;
        };
        if (typeof params.model === 'string' && typeof ce.setModel === 'function') {
          ce.setModel(params.model);
        }
        if (params.weight !== undefined && typeof ce.setWeight === 'function') {
          const w = typeof params.weight === 'number' ? params.weight : parseFloat(String(params.weight));
          ce.setWeight(w);
        }
        if (params.volume !== undefined && typeof ce.setVolume === 'function') {
          const v = typeof params.volume === 'number' ? params.volume : parseFloat(String(params.volume));
          ce.setVolume(v);
        }
        return {
          success: true,
          action,
          estimate: typeof ce.getEstimate === 'function' ? ce.getEstimate() : undefined,
          status: 'Freight cargo estimator reconfigured.',
        };
      }
      return { success: false, action, error: 'loumars-cargo-estimator custom element not found.' };
    }

    if (action === 'queryEcosystemHealth') {
      return getThemeHealthTool.execute({});
    }

    if (action === 'queryWidgetCategories') {
      return getWidgetCategoriesTool.execute(params);
    }

    if (action === 'queryContrast') {
      return getThemeContrastTool.execute(params);
    }

    if (action === 'queryThemeInfo') {
      return getThemeInfoTool.execute(params);
    }

    if (action === 'setThemeMode') {
      return setThemeModeTool.execute(params);
    }

    if (action === 'queryThemeState') {
      const state = getThemeStateTool.execute({});
      return { success: true, action, state, status: 'Theme state retrieved.' };
    }

    if (action === 'querySiteOverview') {
      return siteOverviewTool.execute(params);
    }

    if (action === 'submitContactInquiry') {
      return submitContactInquiryTool.execute(params);
    }

    if (action === 'searchSite') {
      return searchSiteTool.execute(params);
    }

    if (action === 'trackConsignment') {
      return {
        ...(trackConsignmentTool.execute(params) as Record<string, unknown>),
        action,
      };
    }

    if (action === 'calculateEmissions') {
      return {
        ...(calculateEmissionsTool.execute(params) as Record<string, unknown>),
        action,
      };
    }

    if (action === 'queryTradeCorridors') {
      return {
        ...(getTradeCorridorsTool.execute(params) as Record<string, unknown>),
        action,
      };
    }

    if (action === 'queryFleet' || action === 'queryAircraft') {
      return {
        ...(getFleetSpecificationsTool.execute(params) as Record<string, unknown>),
        action,
      };
    }

    if (action === 'queryHubs' || action === 'queryPorts' || action === 'queryGateways') {
      return {
        ...(getLogisticsHubsTool.execute(params) as Record<string, unknown>),
        action,
      };
    }

    if (
      action === 'queryColdChain' ||
      action === 'queryTemperatureZones' ||
      action === 'queryQa'
    ) {
      return {
        ...(getColdChainSpecificationsTool.execute(params) as Record<string, unknown>),
        action,
      };
    }

    if (
      action === 'queryCompliance' ||
      action === 'queryCertifications' ||
      action === 'queryRegulatory'
    ) {
      return {
        ...(getComplianceSpecificationsTool.execute(params) as Record<string, unknown>),
        action,
      };
    }

    if (
      action === 'queryPricing' ||
      action === 'queryPlans' ||
      action === 'queryRateCards'
    ) {
      return {
        ...(getPricingPlansTool.execute(params) as Record<string, unknown>),
        action,
      };
    }

    if (
      action === 'queryCareers' ||
      action === 'queryJobs' ||
      action === 'queryRequisitions'
    ) {
      return {
        ...(getJobPostingsTool.execute(params) as Record<string, unknown>),
        action,
      };
    }

    if (
      action === 'queryCaseStudies' ||
      action === 'queryStories' ||
      action === 'queryPostMortems'
    ) {
      return {
        ...(getCaseStudiesTool.execute(params) as Record<string, unknown>),
        action,
      };
    }

    if (
      action === 'queryTestimonials' ||
      action === 'queryReviews' ||
      action === 'queryEndorsements'
    ) {
      return {
        ...(getTestimonialsTool.execute(params) as Record<string, unknown>),
        action,
      };
    }

    if (
      action === 'queryTeam' ||
      action === 'queryLeadership' ||
      action === 'queryMembers'
    ) {
      return {
        ...(getTeamMembersTool.execute(params) as Record<string, unknown>),
        action,
      };
    }

    if (
      action === 'queryFaq' ||
      action === 'queryAccordion' ||
      action === 'queryDisclosures'
    ) {
      return {
        ...(getFaqTool.execute(params) as Record<string, unknown>),
        action,
      };
    }

    if (action === 'subscribeNewsletter') {
      return {
        ...(subscribeNewsletterTool.execute(params) as Record<string, unknown>),
        action,
      };
    }

    if (action === 'queryNewsletterTopics') {
      return {
        ...(getNewsletterTopicsTool.execute(params) as Record<string, unknown>),
        action,
      };
    }

    if (
      action === 'queryCapabilities' ||
      action === 'querySolutions' ||
      action === 'queryMatrices'
    ) {
      return {
        ...(getCapabilitiesTool.execute(params) as Record<string, unknown>),
        action,
      };
    }

    if (action === 'getShopifyCart' || action === 'queryShopifyCart') {
      return {
        ...(getShopifyCartTool.execute(params) as Record<string, unknown>),
        action,
      };
    }

    if (action === 'setCartCurrency' || action === 'setCurrency') {
      return {
        ...(setCartCurrencyTool.execute(params) as Record<string, unknown>),
        action,
      };
    }

    if (action === 'syncShopifyCart' || action === 'syncCart') {
      return syncShopifyCartTool.execute(params);
    }

    if (action === 'getInventoryLevels' || action === 'queryInventory') {
      return getInventoryLevelsTool.execute(params);
    }

    if (action === 'processInventoryWebhook') {
      return processInventoryWebhookTool.execute(params);
    }

    if (action === 'deprecateInventoryLevel' || action === 'updateInventoryLevel') {
      return deprecateInventoryLevelTool.execute(params);
    }

    if (action === 'getCustomerSession' || action === 'queryCustomerSession') {
      return getCustomerSessionTool.execute(params);
    }

    if (action === 'loginCustomerSso' || action === 'loginCustomer') {
      return loginCustomerSsoTool.execute(params);
    }

    if (action === 'logoutCustomerSso' || action === 'logoutCustomer') {
      return logoutCustomerSsoTool.execute(params);
    }

    return { success: false, action, error: `Unrecognized action "${action}".` };
  },
};

export const allWebMcpTools: WebMcpToolDefinition[] = [
  siteOverviewTool,
  submitContactInquiryTool,
  searchSiteTool,
  trackConsignmentTool,
  calculateEmissionsTool,
  getTradeCorridorsTool,
  getFleetSpecificationsTool,
  getLogisticsHubsTool,
  getColdChainSpecificationsTool,
  getComplianceSpecificationsTool,
  getPricingPlansTool,
  getJobPostingsTool,
  getCaseStudiesTool,
  getTestimonialsTool,
  getTeamMembersTool,
  getFaqTool,
  subscribeNewsletterTool,
  getNewsletterTopicsTool,
  getCapabilitiesTool,
  getShopifyCartTool,
  setCartCurrencyTool,
  syncShopifyCartTool,
  getInventoryLevelsTool,
  processInventoryWebhookTool,
  deprecateInventoryLevelTool,
  getCustomerSessionTool,
  loginCustomerSsoTool,
  logoutCustomerSsoTool,
  getThemeStateTool,
  setThemeModeTool,
  getThemeTokensTool,
  getThemeContrastTool,
  getThemeInfoTool,
  queryThemeWidgetsTool,
  getCanonicalWidgetRegistryTool,
  getThemeHealthTool,
  getWidgetCategoriesTool,
  interactWithWidgetTool,
];

export function registerWebMcpTools(
  nav?: WebMcpNavigator,
  win?: { modelContext?: { registerTool: (tool: WebMcpToolDefinition) => void } }
): boolean {
  const targetNav =
    nav ?? (typeof window !== 'undefined' ? (window.navigator as WebMcpNavigator) : undefined);
  const targetWin =
    win ??
    (typeof window !== 'undefined'
      ? (window as unknown as {
          modelContext?: { registerTool: (tool: WebMcpToolDefinition) => void };
        })
      : undefined);

  const modelContext = targetNav?.modelContext ?? targetWin?.modelContext;
  if (!modelContext || typeof modelContext.registerTool !== 'function') {
    return false;
  }

  try {
    for (const tool of allWebMcpTools) {
      modelContext.registerTool(tool);
    }
    return true;
  } catch (err: unknown) {
    const message = err instanceof Error ? err.message : String(err);
    console.debug('WebMCP registration skipped:', message);
    return false;
  }
}

// Auto-initialize when executing in browser context
if (typeof window !== 'undefined') {
  registerWebMcpTools();
}
